/**
 * Product Display Manager
 * Handles all product filtering, rendering and display logic
 * Following SOLID principles for clean separation of concerns
 */

const MULTI_FORMAT_SELECTION_WARNING_HTML =
  (typeof window !== 'undefined' &&
    window.ProductSystem?.MULTI_FORMAT_SELECTION_WARNING_MARKUP) ||
  '<div class="multi-format-selection-warning" hidden role="status">Heads up! You have selected more than one format for this title.</div>';

/**
 * Stable id for a purchase-block group label (format tab).
 * @param {{ format: string, productId?: string|number, instanceId?: string }} opts
 * @returns {string}
 */
function buildPurchaseGroupLabelId({ format, productId = '', instanceId = '' }) {
  const safeFormat = String(format || 'options').replace(/[^a-zA-Z0-9_-]/g, '');
  const safeProduct = String(productId || '').replace(/[^a-zA-Z0-9_-]/g, '');
  const safeInstance = String(instanceId || '').replace(/[^a-zA-Z0-9_-]/g, '');
  return ['cmrp-purchase-label', safeFormat, safeProduct, safeInstance]
    .filter(Boolean)
    .join('-');
}

/**
 * CMRP format heading (icon + title). Not a control — each format is its own panel.
 * @param {{ format: string, label: string, labelId?: string }} opts
 * @returns {string}
 */
function buildMaterialTitleId({ productId = '', instanceId = '' }) {
  const safeProduct = String(productId || '').replace(/[^a-zA-Z0-9_-]/g, '');
  const safeInstance = String(instanceId || '').replace(/[^a-zA-Z0-9_-]/g, '');
  return ['cmrp-material-title', safeProduct, safeInstance]
    .filter(Boolean)
    .join('-');
}

function buildFormatTabHtml({ format, label, labelId = '' }) {
  const idAttr = labelId ? ` id="${labelId}"` : '';
  return `<h4 class="format-tab active" data-format="${format}"${idAttr}>
                      <span class="format-tab-row format-tab-row--label">
                        <span class="format-tab-icon" aria-hidden="true"></span>
                        <span class="format-tab-title">${label}</span>
                      </span>
                  </h4>`;
}

/**
 * Single-format-style bordered panel (header tab + content).
 * Exposed as one labeled group; purchase options sit beneath the format label.
 * @param {{
 *   format: string,
 *   label: string,
 *   collapseContentHtml: string,
 *   includeWarning?: boolean,
 *   footerHtml?: string,
 *   groupLabelId?: string,
 *   productId?: string|number,
 *   instanceId?: string,
 * }} opts
 * @returns {string}
 */
function buildFormatPurchasePanelHtml({
  format,
  label,
  collapseContentHtml,
  includeWarning = false,
  footerHtml = '',
  groupLabelId = '',
  productId = '',
  instanceId = '',
}) {
  const labelId =
    groupLabelId ||
    buildPurchaseGroupLabelId({ format, productId, instanceId });
  return `
        <div class="purchase-options-container" role="group" aria-labelledby="${labelId}" data-format-panel="${format}">
            ${includeWarning ? MULTI_FORMAT_SELECTION_WARNING_HTML : ''}
            <div class="format-tabs">
                ${buildFormatTabHtml({ format, label, labelId })}
            </div>
            <div class="format-content-area">
                <div class="format-collapse active" data-format="${format}" style="display: block;">
                    <div class="collapse-content">
                        ${collapseContentHtml}
                    </div>
                </div>
            </div>
            ${footerHtml}
        </div>`;
}

class ProductDisplayManager {
  /**
   * @param {Object} config - Configuration object
   * @param {Array<any>} config.products - Products array
   * @param {Array<any>} config.ddcsSection - DDCS section data
   * @param {Array<any>} config.ddcsCampusEntity - Campus entities
   * @param {Array<any>} config.ddcsTerm - Terms data
   * @param {any} config.productHandler - Product handler instance
   */
  constructor(config) {
    this.products = config.products || [];
    this.ddcsSection = config.ddcsSection || [];
    this.ddcsCampusEntity = config.ddcsCampusEntity || [];
    this.ddcsTerm = config.ddcsTerm || [];
    this.productHandler = config.productHandler;

    // Cache DOM elements
    this.globalProductsContainer = null;
    /** @type {number} Incremented each refresh so stale async passes cannot repaint DOM */
    this._refreshGeneration = 0;

    // Bind methods
    this.refreshProductsDisplay = this.refreshProductsDisplay.bind(this);
    this.displayDepartmentSection = this.displayDepartmentSection.bind(this);
    this.displayAdoptionStatusCards =
      this.displayAdoptionStatusCards.bind(this);
    this.displayNormalProductSections =
      this.displayNormalProductSections.bind(this);
    this.getProductsContainer = this.getProductsContainer.bind(this);

    // Initialize adoption status configuration
    this.initializeAdoptionConfig();

  }

  /**
   * Initialize adoption status configuration
   * @private
   */
  initializeAdoptionConfig() {
    this.ADOPTION_STATUS_CONFIG = {
      cancelled: {
        keywords: ['cancelled', 'cancel'],
      },
      'open-resources': {
        keywords: ['open educational resources', 'free'],
      },
      'not-required': {
        keywords: ["aren't required", 'not required'],
      },
      pending: {
        keywords: ['being finalized', 'determined', 'check back'],
      },
    };

    this.ADOPTION_STATUS_ICON =
      '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false"><circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"/><path d="M12 8v4M12 16h.01" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  }

  /**
   * Get or cache the products container element
   * @returns {HTMLElement|null} Products container
   */
  getProductsContainer() {
    if (!this.globalProductsContainer) {
      this.globalProductsContainer =
        document.getElementById('filtered-products');
    }
    return this.globalProductsContainer;
  }
  /**
   * @param {any} metaobjectType
   * @param {any} query
   */
  async fetchMetaobjects(metaobjectType, query) {
    if (typeof window !== 'undefined' && window.fetchMetaobjectsOnce) {
      return window.fetchMetaobjectsOnce(metaobjectType, query);
    }

    const proxyPath = '/apps/shopifyData/metaobjects';
    const url = `${proxyPath}?type=${metaobjectType}&query=${query}`;

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'same-origin',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.success) {
        const activeMetaobjects = result.data.filter(
          // @ts-ignore
          item => item.status === 'ACTIVE',
        );
        return activeMetaobjects;
      } else {
        throw new Error(result.error || 'Failed to fetch metaobjects');
      }
    } catch (error) {
      console.error('Error fetching metaobjects:', error);
      throw error;
    }
  }
  /**
   * @param {any} queryData
   */
  async fetchProducts(queryData) {
    if (typeof window !== 'undefined' && window.fetchProductsOnce) {
      return window.fetchProductsOnce(queryData);
    }

    const proxyPath = '/apps/shopifyData/products';
    const url = `${proxyPath}?query=${queryData}`;

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'same-origin',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.success) {
        return result.products;
      } else {
        throw new Error(result.error || 'Failed to fetch products');
      }
    } catch (error) {
      console.error('Error fetching products:', error);
      throw error;
    }
  }
  /**
   * Stable id for deduping product rows (parallel fetches / duplicate filters).
   * @param {any} product
   * @returns {string}
   */
  static _productDedupeId(product) {
    if (!product) return '';
    return String(
      product.id ||
        product.gid ||
        product.admin_graphql_api_id ||
        product.product_id ||
        ''
    );
  }

  /**
   * Unified-view merge: digital variants keep CEB parentProductId unless stamped.
   * @param {any[]} variants
   * @param {any} parentProduct
   */
  static _stampMergedVariantsParentId(variants, parentProduct) {
    if (!parentProduct?.id || !Array.isArray(variants)) return;
    variants.forEach(v => {
      if (v) v.parentProductId = parentProduct.id;
    });
  }

  static _productIsbn(product) {
    if (!product) return '';
    const mf = product.metafields || {};
    return String(
      mf.isbn_ean ||
        mf.course_materials?.isbn_ean ||
        mf.isbn ||
        mf.isbn13 ||
        ''
    ).trim();
  }

  static _productBookType(product) {
    const mf = product?.metafields || {};
    return mf.book_type_ind || mf.course_materials?.book_type_ind || '';
  }

  static _parsePrintRelationshipIsbns(product) {
    const mf = product?.metafields || {};
    const raw =
      mf.print_relationship_isbn ?? mf.course_materials?.print_relationship_isbn;
    if (raw == null || raw === '') return [];
    if (Array.isArray(raw)) return raw.map(String);
    if (typeof raw === 'string') {
      try {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed.map(String) : [raw];
      } catch {
        return [raw];
      }
    }
    return [];
  }

  static _stampVariantsSourceIsbn(variants, sourceProduct) {
    const isbn = ProductDisplayManager._productIsbn(sourceProduct);
    if (!isbn || !Array.isArray(variants)) return;
    variants.forEach(v => {
      if (v && !v.sourceIsbn) v.sourceIsbn = isbn;
    });
  }

  static _variantDedupeId(variant) {
    if (!variant) return '';
    return String(variant.id || '').split('/').pop();
  }

  /**
   * Choice/Suggested already copy CEB variant ids onto the print parent's
   * variant_digital_status map. Required/Recommended merge must do the same
   * or sticky ATB will treat unified digital rows as print.
   * @param {any} parentProduct
   * @param {any} sourceProduct
   * @param {any[]} variants
   */
  static _copyMergedVariantDigitalStatus(parentProduct, sourceProduct, variants) {
    if (!parentProduct || !Array.isArray(variants) || variants.length === 0) return;
    if (!parentProduct.metafields) parentProduct.metafields = {};
    if (!parentProduct.metafields.custom) parentProduct.metafields.custom = {};
    const existing = parentProduct.metafields.custom.variant_digital_status;
    parentProduct.metafields.custom.variant_digital_status = {
      ...(existing && typeof existing === 'object' ? existing : {}),
    };
    const dest = parentProduct.metafields.custom.variant_digital_status;
    const sourceMap = sourceProduct?.metafields?.custom?.variant_digital_status || {};
    variants.forEach(variant => {
      const variantId = ProductDisplayManager._variantDedupeId(variant);
      if (!variantId) return;
      if (sourceMap[variantId] !== undefined) {
        dest[variantId] = sourceMap[variantId];
      } else if (!(variantId in dest)) {
        dest[variantId] = 'true';
      }
    });
  }

  /**
   * Merge CEB digital products into their TXT print parent (unified CMRP).
   * Used for Required/Recommended, Choice, and Bundle so both formats stack on one card.
   * @param {any[]} products
   * @returns {any[]}
   */
  static _mergeUnifiedCmrpProducts(products) {
    if (!Array.isArray(products) || products.length === 0) {
      return products || [];
    }
    if (!window.shopifySettings?.unifiedViewCMRP) {
      return products;
    }
    return products.reduce((acc, product) => {
      if (ProductDisplayManager._productBookType(product) === 'CEB') {
        const isbnArray = ProductDisplayManager._parsePrintRelationshipIsbns(product);
        if (isbnArray.length > 0) {
          const shouldRemove = isbnArray.some(isbn =>
            products.some(
              item =>
                ProductDisplayManager._productIsbn(item) === String(isbn) &&
                item.material_type === product.material_type
            )
          );
          if (shouldRemove) {
            const variantsToAdd = product.variants || [];
            const parentProduct = products.find(
              item =>
                ProductDisplayManager._productBookType(item) === 'TXT' &&
                item.material_type === product.material_type &&
                isbnArray.some(
                  isbn => ProductDisplayManager._productIsbn(item) === String(isbn)
                )
            );
            if (parentProduct) {
              const existingIds = new Set(
                (parentProduct.variants || []).map(
                  ProductDisplayManager._variantDedupeId
                )
              );
              const newVariants = variantsToAdd.filter(v => {
                const id = ProductDisplayManager._variantDedupeId(v);
                return id && !existingIds.has(id);
              });
              ProductDisplayManager._stampVariantsSourceIsbn(
                newVariants,
                product
              );
              parentProduct.variants = [
                ...(parentProduct.variants || []),
                ...newVariants,
              ];
              ProductDisplayManager._copyMergedVariantDigitalStatus(
                parentProduct,
                product,
                newVariants
              );
              ProductDisplayManager._stampMergedVariantsParentId(
                parentProduct.variants,
                parentProduct
              );
            }
            return acc;
          }
        }
      }
      acc.push(product);
      return acc;
    }, []);
  }

  /**
   * Choice is one CMRP display slot. Required Choice (Choice / Choice/Required)
   * sorts with Required, before Recommended. Choice/Recommended-only groups
   * sort with Recommended. Options inside the slot are sorted Required →
   * Recommended, then alphabetically by title.
   * @param {any[]} choiceProducts
   * @returns {'Required' | 'Recommended'}
   */
  static _choiceSlotMaterialType(choiceProducts) {
    if (!Array.isArray(choiceProducts) || choiceProducts.length === 0) {
      return 'Required';
    }
    const hasRequiredChoice = choiceProducts.some(p => {
      const t = p?.material_type;
      return t === 'Choice' || t === 'Choice/Required';
    });
    if (hasRequiredChoice) return 'Required';
    const allRecommended = choiceProducts.every(
      p => p?.material_type === 'Choice/Recommended'
    );
    return allRecommended ? 'Recommended' : 'Required';
  }

  static _materialTypeSortRank(materialType) {
    const order = {
      Required: 1,
      Choice: 1,
      'Choice/Required': 1,
      Recommended: 2,
      'Choice/Recommended': 2,
      Suggested: 3,
    };
    return order[materialType] || 4;
  }

  static _productSortTitle(product) {
    return String(product?.title || '').trim();
  }

  /**
   * Nested sort: Required → Recommended → Suggested, then title A–Z within each group.
   * @param {any} a
   * @param {any} b
   * @returns {number}
   */
  static _compareProductsByMaterialThenTitle(a, b) {
    const rankDiff =
      ProductDisplayManager._materialTypeSortRank(a?.material_type) -
      ProductDisplayManager._materialTypeSortRank(b?.material_type);
    if (rankDiff !== 0) return rankDiff;
    return ProductDisplayManager._productSortTitle(a).localeCompare(
      ProductDisplayManager._productSortTitle(b),
      undefined,
      { sensitivity: 'base' }
    );
  }

  /**
   * @param {any[]} products
   * @returns {any[]}
   */
  static _sortProductsByMaterialThenTitle(products) {
    if (!Array.isArray(products) || products.length <= 1) {
      return products || [];
    }
    return products
      .slice()
      .sort(ProductDisplayManager._compareProductsByMaterialThenTitle);
  }

  /**
   * Slot order: Required → Recommended, alphabetized product/bundle cards
   * within each group, then the Choice slot for that group.
   * @param {{ kind: string, materialType: string, row?: { parentProduct?: any } }} a
   * @param {{ kind: string, materialType: string, row?: { parentProduct?: any } }} b
   * @returns {number}
   */
  static _compareDisplaySlots(a, b) {
    const rankDiff =
      ProductDisplayManager._materialTypeSortRank(a?.materialType) -
      ProductDisplayManager._materialTypeSortRank(b?.materialType);
    if (rankDiff !== 0) return rankDiff;
    if (a?.kind !== b?.kind) {
      return a?.kind === 'choice' ? 1 : -1;
    }
    if (a?.kind === 'product') {
      return ProductDisplayManager._compareProductsByMaterialThenTitle(
        a.row?.parentProduct,
        b.row?.parentProduct
      );
    }
    return 0;
  }

  /** DDCS boolean flag (Print Section/Course Note On Tag). */
  static _parsePrintNoteOnTagFlag(obj, ...keys) {
    if (!obj || typeof obj !== 'object') return false;
    for (const key of keys) {
      const raw = obj[key] ?? obj.fields?.[key];
      if (raw == null) continue;
      const val = typeof raw === 'object' && raw !== null && 'value' in raw ? raw.value : raw;
      if (typeof val === 'boolean') return val;
      const s = String(val).trim().toLowerCase();
      if (s === 'true' || s === '1' || s === 'yes') return true;
      if (s === 'false' || s === '0' || s === 'no') return false;
    }
    return false;
  }

  static _canPrintSectionNoteOnTag(sectionRec) {
    return ProductDisplayManager._parsePrintNoteOnTagFlag(
      sectionRec,
      'print_section_note_on_tag_flag',
    );
  }

  static _canPrintCourseNoteOnTag(courseRec) {
    return ProductDisplayManager._parsePrintNoteOnTagFlag(
      courseRec,
      'print_course_note_on_tag_flag',
    );
  }

  static _NOTE_NA_VALUES = ['N/A', 'NA', 'na', 'n/a'];
  static _NOTE_DESKTOP_TRUNCATE = 400;
  static _NOTE_MOBILE_TRUNCATE = 100;
  static _NOTE_MAX_CHARS = 4000;

  /** Get .note from metaobject; try note/notes and accept string or value object */
  static _getMetaobjectNote(obj) {
    if (!obj || typeof obj !== 'object') return null;
    const raw =
      obj.note ??
      obj.notes ??
      obj.Note ??
      obj.Notes ??
      obj.fields?.note ??
      obj.fields?.notes;
    if (raw == null) return null;
    const str = typeof raw === 'string' ? raw : (raw?.value ?? String(raw));
    return typeof str === 'string' && str.trim() !== '' ? str.trim() : null;
  }

  /** Match section_d value (handles nested .value and .fields) */
  static _sectionDVal(c) {
    const v =
      c?.section_d ??
      c?.fields?.section_d ??
      c?.section_id ??
      c?.fields?.section_id;
    return v != null
      ? String(typeof v === 'object' && v.value !== undefined ? v.value : v).trim()
      : '';
  }

  /** Match course_id value (handles nested .value and .fields) */
  static _courseIdVal(c) {
    const v = c?.course_id ?? c?.fields?.course_id;
    return v != null
      ? String(typeof v === 'object' && v.value !== undefined ? v.value : v).trim()
      : '';
  }

  static _isValidCourseNote(note) {
    if (note == null || typeof note !== 'string') return false;
    const t = note.trim();
    return t.length > 0 && !ProductDisplayManager._NOTE_NA_VALUES.includes(t);
  }

  static _truncateNoteAtBlank(text, maxLen) {
    if (!text || text.length <= maxLen) return text;
    const slice = text.slice(0, maxLen);
    const lastSpace = slice.lastIndexOf(' ');
    if (lastSpace <= 0) return slice;
    return slice.slice(0, lastSpace);
  }

  static _getNoteTruncateLimit() {
    if (typeof window === 'undefined' || !window.matchMedia) {
      return ProductDisplayManager._NOTE_DESKTOP_TRUNCATE;
    }
    return window.matchMedia('(min-width: 768px)').matches
      ? ProductDisplayManager._NOTE_DESKTOP_TRUNCATE
      : ProductDisplayManager._NOTE_MOBILE_TRUNCATE;
  }

  static _createCourseNoteElement(rawNote, modifierClass) {
    const fullNote = String(rawNote)
      .trim()
      .slice(0, ProductDisplayManager._NOTE_MAX_CHARS);

    const wrap = document.createElement('div');
    wrap.className = 'course-section-note ' + (modifierClass || '');
    wrap.dataset.fullNote = fullNote;
    wrap.tabIndex = 0;
    wrap.setAttribute('role', 'note');
    const noteKind = String(modifierClass || '').includes('--course')
      ? 'Course note'
      : 'Section note';
    wrap.setAttribute('aria-label', `${noteKind}: ${fullNote}`);
    const textSpan = document.createElement('span');
    textSpan.className = 'course-section-note__text';
    const linkSpan = document.createElement('span');
    linkSpan.className = 'course-section-note__toggle';

    const setContent = expanded => {
      const displayLimit = ProductDisplayManager._getNoteTruncateLimit();
      const displayTruncated = ProductDisplayManager._truncateNoteAtBlank(
        fullNote,
        displayLimit
      );
      const showFull = expanded || fullNote.length <= displayLimit;
      const displayText = showFull ? fullNote : displayTruncated;
      const showLink = fullNote.length > displayLimit;
      textSpan.textContent = displayText;
      linkSpan.innerHTML = '';
      linkSpan.style.display = 'none';
      if (showLink) {
        const link = document.createElement('button');
        link.type = 'button';
        link.className = 'course-section-note__toggle-link';
        link.setAttribute('aria-expanded', showFull ? 'true' : 'false');
        if (showFull) {
          link.textContent = ' Show Less';
          link.addEventListener('click', () => setContent(false));
        } else {
          link.textContent = ' Show More';
          link.addEventListener('click', () => setContent(true));
        }
        linkSpan.style.display = 'inline';
        linkSpan.appendChild(link);
      }
    };

    setContent(false);
    wrap.appendChild(textSpan);
    wrap.appendChild(linkSpan);
    return wrap;
  }

  /**
   * @param {string|null} sectionNote
   * @param {string|null} courseNote
   * @returns {HTMLElement|null}
   */
  static _buildSectionCourseNotesElement(sectionNote, courseNote) {
    const wrap = document.createElement('div');
    wrap.className = 'course-section-notes';
    if (ProductDisplayManager._isValidCourseNote(sectionNote)) {
      wrap.appendChild(
        ProductDisplayManager._createCourseNoteElement(
          sectionNote,
          'course-section-note--section'
        )
      );
    }
    if (ProductDisplayManager._isValidCourseNote(courseNote)) {
      wrap.appendChild(
        ProductDisplayManager._createCourseNoteElement(
          courseNote,
          'course-section-note--course'
        )
      );
    }
    return wrap.children.length > 0 ? wrap : null;
  }

  /**
   * Resolve printable section/course notes from DDCS records.
   * @param {string} sectionId
   * @param {any} [sectionInfo]
   * @param {any[]} [ddcsCourseList]
   * @returns {{ sectionNote: string|null, courseNote: string|null }}
   */
  static _resolvePrintableSectionCourseNotes(
    sectionId,
    sectionInfo,
    ddcsCourseList
  ) {
    const sid = String(sectionId ?? '');
    const ddcsCourse = Array.isArray(ddcsCourseList)
      ? ddcsCourseList
      : window.courseData?.ddcs_course ?? [];
    const ddcsSection = window.courseData?.ddcs_section ?? [];

    let sectionRec = sectionInfo;
    if (!sectionRec && sid) {
      ProductDisplayManager._ensureDdcsIndexes();
      sectionRec = ProductDisplayManager._sectionsById?.get(sid);
      if (!sectionRec && ddcsSection.length) {
        sectionRec = ddcsSection.find(
          s =>
            ProductDisplayManager._sectionDVal(s) === sid ||
            String(s?.section_id ?? s?.section_d ?? '') === sid
        );
      }
    }

    let sectionMeta = ddcsCourse.find(
      c => ProductDisplayManager._sectionDVal(c) === sid
    );
    if (sectionMeta == null) {
      sectionMeta = sectionRec;
    }

    let sectionNote = null;
    if (ProductDisplayManager._canPrintSectionNoteOnTag(sectionRec)) {
      sectionNote = ProductDisplayManager._getMetaobjectNote(sectionMeta);
    }

    const courseId =
      sectionRec?.course_id != null ? String(sectionRec.course_id) : '';
    const courseMeta = courseId
      ? ddcsCourse.find(c => ProductDisplayManager._courseIdVal(c) === courseId)
      : null;
    let courseNote = null;
    if (ProductDisplayManager._canPrintCourseNoteOnTag(courseMeta)) {
      courseNote = ProductDisplayManager._getMetaobjectNote(courseMeta);
    }

    return { sectionNote, courseNote };
  }

  /**
   * @param {any} product
   * @returns {Array<Record<string, unknown>>}
   */
  static _parseSectionDataMapping(product) {
    const raw = product?.metafields?.course_materials?.section_data_mapping;
    if (!raw) return [];
    try {
      const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  /**
   * @param {any} product
   * @returns {string[]}
   */
  static _productDdcsSectionGids(product) {
    /** @type {string[]} */
    const gids = [];
    const mf = product?.metafields;
    const candidates = [
      mf?.ddcs_section,
      mf?.ddcs?.ddcs_section,
      mf?.course_materials?.ddcs_section,
    ];
    for (const candidate of candidates) {
      if (!candidate) continue;
      if (typeof candidate === 'string') {
        try {
          const parsed = JSON.parse(candidate);
          if (Array.isArray(parsed)) {
            parsed.forEach(x => gids.push(String(x)));
          } else {
            gids.push(String(candidate));
          }
        } catch {
          gids.push(String(candidate));
        }
      } else if (Array.isArray(candidate)) {
        candidate.forEach(x => {
          if (typeof x === 'string') gids.push(x);
          else if (x?.gid) gids.push(String(x.gid));
        });
      }
    }
    return gids;
  }

  /**
   * Post-fetch filter: product section labels vs filter.selectedsection.
   * @param {any} product
   * @param {{ selectedsection?: string, selectedsectionId?: string, selectedsectionGid?: string }} filter
   * @returns {boolean}
   */
  static _productSectionMatchesFilter(product, filter) {
    if (!product || !filter) return false;
    const sectionLabel = String(filter.selectedsection ?? '').trim();
    if (!sectionLabel) return true;

    const norm = ProductDisplayManager._normalizeCmrpCompareKey;
    if (norm(product._cmrpFetchSectionLabel) === norm(sectionLabel)) return true;

    const sections = Array.isArray(product.section) ? product.section : [];
    if (sections.some(s => norm(s) === norm(sectionLabel))) return true;

    const sectionId = String(filter.selectedsectionId ?? '').trim();
    if (sectionId) {
      if (String(product._cmrpFetchSectionId ?? '') === sectionId) return true;
      const mapping = ProductDisplayManager._parseSectionDataMapping(product);
      if (mapping.some(m => String(m?.SectionID ?? '') === sectionId)) return true;
    }

    const sectionGid = String(filter.selectedsectionGid ?? '').trim();
    if (
      sectionGid &&
      norm(product._cmrpFetchSectionGid) === norm(sectionGid)
    ) {
      return true;
    }

    return false;
  }

  /**
   * Row gate: attach products to the correct section card (sectionId / GID / mapping).
   * @param {any} product
   * @param {{ selectedsection?: string, selectedsectionId?: string, selectedsectionGid?: string }} courseInfo
   * @returns {boolean}
   */
  static _productRowMatchesCourseSectionIds(product, courseInfo) {
    if (!product) return false;
    const sectionIdNeedle = String(courseInfo?.selectedsectionId ?? '').trim();
    const sectionGidNeedle = String(courseInfo?.selectedsectionGid ?? '').trim();
    if (!sectionIdNeedle && !sectionGidNeedle) return true;

    const norm = ProductDisplayManager._normalizeCmrpCompareKey;
    const fetchId = String(product._cmrpFetchSectionId ?? '').trim();
    const fetchGid = String(product._cmrpFetchSectionGid ?? '').trim();

    if (fetchId || fetchGid) {
      if (
        sectionIdNeedle &&
        fetchId &&
        (fetchId === sectionIdNeedle || fetchId.includes(sectionIdNeedle))
      ) {
        return true;
      }
      if (
        sectionGidNeedle &&
        fetchGid &&
        norm(fetchGid) === norm(sectionGidNeedle)
      ) {
        return true;
      }
      return false;
    }

    const sectionIds = product?.sectionId;
    if (sectionIds != null && sectionIds !== '') {
      if (Array.isArray(sectionIds)) {
        if (
          sectionIdNeedle &&
          sectionIds.some(id => String(id).includes(sectionIdNeedle))
        ) {
          return true;
        }
      } else if (
        sectionIdNeedle &&
        String(sectionIds).includes(sectionIdNeedle)
      ) {
        return true;
      }
    }

    if (sectionIdNeedle) {
      const mapping = ProductDisplayManager._parseSectionDataMapping(product);
      if (mapping.some(m => String(m?.SectionID ?? '') === sectionIdNeedle)) {
        return true;
      }
    }

    if (sectionGidNeedle) {
      const gids = ProductDisplayManager._productDdcsSectionGids(product);
      if (gids.some(g => norm(g) === norm(sectionGidNeedle))) return true;
    }

    const hasSectionIds =
      sectionIds != null &&
      sectionIds !== '' &&
      (!Array.isArray(sectionIds) || sectionIds.length > 0);
    if (!hasSectionIds) {
      return ProductDisplayManager._productSectionMatchesFilter(product, courseInfo);
    }

    return ProductDisplayManager._productSectionMatchesFilter(product, courseInfo);
  }

  /**
   * Shallow-clone products and stamp the filter section they were fetched for.
   * @param {Array<any>} products
   * @param {any} filter
   * @returns {Array<any>}
   */
  static _tagProductsForFilter(products, filter) {
    if (!Array.isArray(products) || !filter) return products || [];
    const sectionId = String(filter.selectedsectionId ?? '').trim();
    const sectionGid = String(filter.selectedsectionGid ?? '').trim();
    const sectionLabel = String(filter.selectedsection ?? '').trim();
    if (!sectionId && !sectionGid && !sectionLabel) return products;

    return products.map(product => {
      if (!product) return product;
      /** @type {any} */
      const tagged = { ...product };
      tagged._cmrpFetchSectionId = sectionId;
      tagged._cmrpFetchSectionGid = sectionGid;
      tagged._cmrpFetchSectionLabel = sectionLabel;

      /** @type {Set<string>} */
      const idSet = new Set();
      const existing = tagged.sectionId;
      const existingArr = Array.isArray(existing)
        ? existing
        : existing != null && existing !== ''
          ? [existing]
          : [];
      existingArr.forEach(id => {
        const s = String(id).trim();
        if (s) idSet.add(s);
      });
      if (sectionId) idSet.add(sectionId);
      if (idSet.size) {
        tagged.sectionId = [...idSet];
      }

      return tagged;
    });
  }

  /**
   * @param {Array<any>} products
   * @param {any} filter
   * @returns {Array<any>}
   */
  _prepareFetchedProducts(products, filter) {
    const live = (products || []).filter(
      product => product && product.status !== 'DRAFT'
    );
    return ProductDisplayManager._tagProductsForFilter(live, filter);
  }

  /**
   * Align CMRP filter strings with DDCS metaobject text (entities, URL-escaped ampersands, spacing).
   * @param {unknown} value
   * @returns {string}
   */
  static _normalizeCmrpCompareKey(value) {
    return String(value ?? '')
      .replace(/%26/gi, '&')
      .replace(/&amp;/gi, '&')
      .replace(/&lt;/gi, '<')
      .replace(/&gt;/gi, '>')
      .replace(/&quot;/gi, '"')
      .replace(/&#0*39;/gi, "'")
      .replace(/&#x0*27;/gi, "'")
      .replace(/&apos;/gi, "'")
      .toLowerCase()
      .replace(/\s+/g, '');
  }

  /** Enable in console: window.PDM_PERF_DEBUG = true */
  static _perfEnabled() {
    return typeof window !== 'undefined' && window.PDM_PERF_DEBUG === true;
  }

  /** @param {string} label */
  static _perfStart(label) {
    if (!ProductDisplayManager._perfEnabled()) return null;
    return { label, t0: performance.now() };
  }

  /**
   * @param {{ label: string, t0: number } | null} token
   * @param {Record<string, unknown>} [extra]
   */
  static _perfEnd(token, extra) {
    if (!token) return;
    const ms = performance.now() - token.t0;
  }

  /** @type {number | null} */
  static _ddcsCacheVersion = null;
  /** @type {any[] | null} */
  static _indexSectionsRef = null;
  /** @type {any[] | null} */
  static _indexCoursesRef = null;
  /** @type {any[] | null} */
  static _indexDeptsRef = null;
  /** @type {any[] | null} */
  static _indexDivsRef = null;
  /** @type {Map<string, any> | null} */
  static _sectionsById = null;
  /** @type {Map<string, any> | null} */
  static _sectionsByGid = null;
  /** @type {Map<string, any[]> | null} */
  static _sectionsByCampusTermName = null;
  /** @type {Map<string, any[]> | null} */
  static _sectionsByCampusTermSectionId = null;
  /** @type {Map<string, any[]> | null} */
  static _coursesByCampusCourseId = null;
  /** @type {Map<string, any[]> | null} */
  static _deptsByCampusDeptId = null;
  /** @type {Map<string, any[]> | null} */
  static _divsByCampusDivId = null;
  /** @type {WeakMap<object, number> | null} */
  static _sectionSourceIndex = null;
  /** @type {Map<string, number> | null} */
  static _departmentSectionCounts = null;

  /** @param {Map<string, any[]>} map @param {string} key @param {any} item */
  static _pushIndex(map, key, item) {
    let arr = map.get(key);
    if (!arr) {
      arr = [];
      map.set(key, arr);
    }
    arr.push(item);
  }

  static _ensureDdcsIndexes() {
    const sections = window.courseData?.ddcs_section;
    const courses = window.courseData?.ddcs_course;
    const depts = window.courseData?.ddcs_campus_department;
    const divs = window.courseData?.ddcs_campus_division;
    const version = Array.isArray(sections) ? sections.length : 0;
    if (
      ProductDisplayManager._indexSectionsRef === sections &&
      ProductDisplayManager._indexCoursesRef === courses &&
      ProductDisplayManager._indexDeptsRef === depts &&
      ProductDisplayManager._indexDivsRef === divs &&
      ProductDisplayManager._sectionsByCampusTermName
    ) {
      return;
    }
    ProductDisplayManager._ddcsCacheVersion = version;
    ProductDisplayManager._indexSectionsRef = sections;
    ProductDisplayManager._indexCoursesRef = courses;
    ProductDisplayManager._indexDeptsRef = depts;
    ProductDisplayManager._indexDivsRef = divs;
    ProductDisplayManager._sectionsById = new Map();
    ProductDisplayManager._sectionsByGid = new Map();
    ProductDisplayManager._sectionsByCampusTermName = new Map();
    ProductDisplayManager._sectionsByCampusTermSectionId = new Map();
    ProductDisplayManager._coursesByCampusCourseId = new Map();
    ProductDisplayManager._deptsByCampusDeptId = new Map();
    ProductDisplayManager._divsByCampusDivId = new Map();
    ProductDisplayManager._sectionSourceIndex = new WeakMap();
    ProductDisplayManager._departmentSectionCounts = new Map();

    const normalize = v => ProductDisplayManager._normalizeCmrpCompareKey(v);
    const coursesById = new Map();
    for (const course of courses || []) {
      coursesById.set(normalize(course.course_id), course);
      ProductDisplayManager._pushIndex(
        ProductDisplayManager._coursesByCampusCourseId,
        `${normalize(course.campus_entity_id)}|${normalize(course.course_id)}`,
        course
      );
    }
    const deptsById = new Map();
    for (const dept of depts || []) {
      deptsById.set(normalize(dept.campus_department_id), dept);
      ProductDisplayManager._pushIndex(
        ProductDisplayManager._deptsByCampusDeptId,
        `${normalize(dept.campus_entity_id)}|${normalize(
          dept.campus_department_id
        )}`,
        dept
      );
    }
    for (const division of divs || []) {
      ProductDisplayManager._pushIndex(
        ProductDisplayManager._divsByCampusDivId,
        `${normalize(division.campus_entity_id)}|${normalize(
          division.campus_division_id
        )}`,
        division
      );
    }

    const buildStart = ProductDisplayManager._perfStart('ddcs-index-build');
    for (let i = 0; i < (sections || []).length; i++) {
      const section = sections[i];
      if (section && typeof section === 'object') {
        ProductDisplayManager._sectionSourceIndex.set(section, i);
      }
      if (section.section_id != null && section.section_id !== '') {
        ProductDisplayManager._sectionsById.set(
          String(section.section_id),
          section
        );
      }
      const gidKey = normalize(section.gid);
      if (gidKey && !ProductDisplayManager._sectionsByGid.has(gidKey)) {
        ProductDisplayManager._sectionsByGid.set(gidKey, section);
      }
      const campusTerm = `${normalize(section.campus_entity_id)}|${normalize(
        section.term_id
      )}`;
      ProductDisplayManager._pushIndex(
        ProductDisplayManager._sectionsByCampusTermName,
        `${campusTerm}|${normalize(section.name)}`,
        section
      );
      ProductDisplayManager._pushIndex(
        ProductDisplayManager._sectionsByCampusTermSectionId,
        `${campusTerm}|${normalize(section.section_id || '')}`,
        section
      );
      const course = coursesById.get(normalize(section.course_id));
      const dept = course
        ? deptsById.get(normalize(course.campus_department_id))
        : null;
      const deptName = normalize(dept?.name || '');
      if (deptName) {
        ProductDisplayManager._departmentSectionCounts.set(
          deptName,
          (ProductDisplayManager._departmentSectionCounts.get(deptName) || 0) +
            1
        );
      }
    }
    ProductDisplayManager._perfEnd(buildStart, {
      sections: version,
      courses: Array.isArray(courses) ? courses.length : 0,
      departments: Array.isArray(depts) ? depts.length : 0,
    });
  }

  /**
   * Same matches as scanning ddcs_section, in original array order.
   * @param {string} campusId
   * @param {string} termId
   * @param {string} sectionKey
   * @param {string} [storeNumber]
   * @returns {any[]}
   */
  static _ddcsCandidateSections(campusId, termId, sectionKey, storeNumber) {
    ProductDisplayManager._ensureDdcsIndexes();
    const n = ProductDisplayManager._normalizeCmrpCompareKey;
    const key = `${n(campusId)}|${n(termId)}|${n(sectionKey || '')}`;
    const fromName =
      ProductDisplayManager._sectionsByCampusTermName?.get(key) || [];
    const fromId =
      ProductDisplayManager._sectionsByCampusTermSectionId?.get(key) || [];
    const seen = new Set();
    const out = [];
    const add = section => {
      const id = section.gid || section.section_id;
      const seenKey = id != null && id !== '' ? String(id) : section;
      if (seen.has(seenKey)) return;
      seen.add(seenKey);
      if (storeNumber && n(section.store_number) !== n(storeNumber)) return;
      out.push(section);
    };
    for (let i = 0; i < fromName.length; i++) add(fromName[i]);
    for (let i = 0; i < fromId.length; i++) add(fromId[i]);
    const order = ProductDisplayManager._sectionSourceIndex;
    if (order && out.length > 1) {
      out.sort((a, b) => (order.get(a) ?? 0) - (order.get(b) ?? 0));
    }
    return out;
  }

  /**
   * O(1) lookup — avoids scanning all ddcs_section on every render.
   * @param {string} departmentName
   */
  static getDepartmentSectionCount(departmentName) {
    ProductDisplayManager._ensureDdcsIndexes();
    return (
      ProductDisplayManager._departmentSectionCounts?.get(
        ProductDisplayManager._normalizeCmrpCompareKey(departmentName)
      ) || 0
    );
  }

  /** @type {Promise<boolean> | null} */
  static _digitalDeliveryNoticeVisiblePromise = null;

  /** @returns {Promise<boolean>} */
  static getDigitalDeliveryNoticeVisible() {
    if (!ProductDisplayManager._digitalDeliveryNoticeVisiblePromise) {
      ProductDisplayManager._digitalDeliveryNoticeVisiblePromise =
        typeof window.isDigitalDeliveryNoticeStoreEnabled === 'function'
          ? window.isDigitalDeliveryNoticeStoreEnabled()
          : Promise.resolve(false);
    }
    return ProductDisplayManager._digitalDeliveryNoticeVisiblePromise;
  }

  /**
   * Collapse duplicate course lines (same campus/term/dept/course/section after normalize).
   * Keeps first occurrence order; prefers the entry with section GID / adoption filled in.
   * @param {Array<any>} filters
   * @returns {Array<any>}
   */
  dedupeSelectedFilters(filters) {
    if (!Array.isArray(filters) || filters.length < 2) {
      return filters || [];
    }
    const normalize = v => ProductDisplayManager._normalizeCmrpCompareKey(v);
    const keyOf = f =>
      [
        normalize(f.selectedCampus),
        normalize(f.selectedTerm),
        normalize(f.selectedDivision),
        normalize(f.selectedDepartment),
        normalize(f.selectedcourse),
        normalize(f.selectedsection),
      ].join('\u0001');
    const score = x =>
      (x.selectedsectionGid ? 2 : 0) +
      (x.selectedsectionId ? 1 : 0) +
      (x.adoption_status ? 1 : 0);
    /** @type {Map<string, any>} */
    const map = new Map();
    const order = [];
    for (const f of filters) {
      if (!f) continue;
      const k = keyOf(f);
      if (!map.has(k)) {
        map.set(k, f);
        order.push(k);
        continue;
      }
      const prev = map.get(k);
      if (score(f) > score(prev)) {
        map.set(k, f);
      }
    }
    return order.map(k => map.get(k));
  }

  /**
   * Main function to refresh and display products based on selected filters
   * @param {Array<any>} selectedFilters - Current selected filters
   */
  
  async refreshProductsDisplay(selectedFilters) {
    const generation = ++this._refreshGeneration;
    const perfTotal = ProductDisplayManager._perfStart('refreshProductsDisplay (total)');
    const perfStats = {
      filters: 0,
      sectionScans: 0,
      courseScans: 0,
      deptScans: 0,
      fetchCalls: 0,
      productsFetched: 0,
    };

    // Defensive programming: ensure selectedFilters is defined and is an array
    if (!selectedFilters || !Array.isArray(selectedFilters)) {
      selectedFilters = [];
    }

    const productsContainer = this.getProductsContainer();
    if (!productsContainer) return;

    if (selectedFilters.length > 0) {
      const deduped = this.dedupeSelectedFilters(selectedFilters);
      if (deduped.length !== selectedFilters.length) {
        selectedFilters.splice(0, selectedFilters.length, ...deduped);
      }
    }

    // Remove prior course sections and department titles immediately when starting a
    // non-empty refresh (avoids stale list/chips while fetches run or if an older refresh wins).
    if (selectedFilters.length > 0) {
      productsContainer.innerHTML = '';
    }

    if (selectedFilters.length === 0) {
      if (generation !== this._refreshGeneration) {
        return;
      }
      // Stale fetched rows would keep the Course Materials List header visible.
      if (window.courseData) {
        window.courseData.products_Materials = [];
      }
      this.showEmptyState();
      productsContainer.classList.remove('course-layout');
      productsContainer.innerHTML = '';
      window.courseMaterialsPageInstance?.components?.navigationManager?.hideNavigationPanels?.();
      await this.handleEmptyFiltersState();
      this.hideLoadingState();
      await window.generateDepartmentChips?.();
      if (generation !== this._refreshGeneration) {
        return;
      }
      if (
        window.courseMaterialsSidebar &&
        typeof window.courseMaterialsSidebar.syncBubblesFromFilters === 'function'
      ) {
        window.courseMaterialsSidebar.syncBubblesFromFilters([]);
      }
      document.dispatchEvent(
        new CustomEvent('product-display:updated', { bubbles: true })
      );
      return;
    }

    if (generation !== this._refreshGeneration) {
      return;
    }
    
    // One clear before parallel fetches; do not reset inside each task (would race and duplicate rows).
    // @ts-ignore
    window.courseData.products_Materials = [];

    perfStats.filters = selectedFilters.length;

    /** @type {any[]} Filters whose course/section could not be resolved in DDCS */
    const courseNotFoundFilters = [];
    /** @type {any[]} Filters whose division could not be resolved in DDCS */
    const divisionNotFoundFilters = [];

    const perfFetch = ProductDisplayManager._perfStart('parallel filter resolve + fetchProducts');
    const promises = selectedFilters.map(async (item, filterIndex) => {
      const perfFilter = ProductDisplayManager._perfStart(
        `filter[${filterIndex}] resolve`
      );
      try {
      if (item?._cmrpUrlNotFound) {
        ProductDisplayManager._perfEnd(perfFilter, { result: 'url not found' });
        courseNotFoundFilters.push(item);
        return [];
      }
      const normalize = v => ProductDisplayManager._normalizeCmrpCompareKey(v);

      // Same campus resolution as `dev`: match by campus display name first, then
      // sidebar. Prefer `item.campus_entity_id` only when a store_number is present
      // (parent+child) so a mismatched id cannot drop every same-campus URL line.
      const campusMatch = (window.courseData.ddcs_campus_entity || []).find(
        campus => normalize(campus.name) === normalize(item.selectedCampus),
      );
      let campusId = campusMatch?.campus_entity_id || '';
      if (!campusId && item.store_number) {
        campusId = String(item.campus_entity_id || '').trim();
      }
      if (!campusId) {
        campusId =
          window.courseMaterialsSidebar?.campus_entity_id ||
          String(item.campus_entity_id || '').trim() ||
          '';
      }
      if (!campusId) {
        ProductDisplayManager._perfEnd(perfFilter, { result: 'no campusId' });
        courseNotFoundFilters.push(item);
        console.warn('[CMRP restore] drop: no campusId', {
          i: filterIndex,
          campus: item.selectedCampus,
          dept: item.selectedDepartment,
          course: item.selectedcourse,
          section: item.selectedsection,
          campus_entity_id: item.campus_entity_id,
        });
        return [];
      }
      // Fast path: the form (or a prior resolve) already stamped the section GID.
      // Do not require adoption_status — View Materials never set that field, so
      // name matching re-ran and dropped lines. Fill status/id from DDCS when missing.
      if (item.selectedsectionGid) {
        const gid = String(item.selectedsectionGid).trim();
        if (gid) {
        ProductDisplayManager._ensureDdcsIndexes();
        const sec =
          ProductDisplayManager._sectionsByGid?.get(normalize(gid)) ||
          (window.courseData.ddcs_section || []).find(
            section => normalize(section.gid) === normalize(gid)
          );
        if (sec) {
          if (!item.selectedsectionId) {
            item.selectedsectionId = sec.section_id || '';
          }
          if (!item.hasOwnProperty('adoption_status')) {
            item.adoption_status =
              sec.adoption_status != null && sec.adoption_status !== ''
                ? String(sec.adoption_status)
                : '';
          }
        } else if (!item.hasOwnProperty('adoption_status')) {
          item.adoption_status = '';
        }
        const queryDataProd =
          "metafields.ddcs.ddcs_section:'" + gid + "'";

        const newProducts = await this.fetchProducts(queryDataProd);
        perfStats.fetchCalls += 1;
        const taggedProducts = this._prepareFetchedProducts(newProducts, item);
        perfStats.productsFetched += taggedProducts.length;
        ProductDisplayManager._perfEnd(perfFilter, {
          path: 'gid-only fetch',
          products: taggedProducts.length,
        });
        return taggedProducts;
        }
      }

      // Resolve candidates section-first, then course → department → (optional) division
      // before locking the matched section (division disambiguates when present).
      const nCampus = normalize(campusId);
      const candidateSections = ProductDisplayManager._ddcsCandidateSections(
        campusId,
        item.selectedTermId,
        item.selectedsection || '',
        item.store_number
      );
      perfStats.sectionScans += candidateSections.length;

      if (!candidateSections.length) {
        ProductDisplayManager._perfEnd(perfFilter, { result: 'no section' });
        courseNotFoundFilters.push(item);
        console.warn('[CMRP restore] drop: no section', {
          i: filterIndex,
          campusId,
          termId: item.selectedTermId,
          dept: item.selectedDepartment,
          course: item.selectedcourse,
          section: item.selectedsection,
          gid: item.selectedsectionGid,
          ddcsSections: window.courseData?.ddcs_section?.length,
        });
        return [];
      }

      /** @type {Array<{ section: any, course: any }>} */
      const matchedCourses = [];

      for (let s = 0; s < candidateSections.length; s++) {
        const section = candidateSections[s];
        const coursesForSection =
          ProductDisplayManager._coursesByCampusCourseId?.get(
            `${nCampus}|${normalize(section.course_id)}`
          ) || [];
        perfStats.courseScans += coursesForSection.length;
        for (let c = 0; c < coursesForSection.length; c++) {
          const course = coursesForSection[c];
          if (course.disabled_flag) continue;
          if (normalize(course.coursenumber) !== normalize(item.selectedcourse)) {
            continue;
          }
          if (
            item.store_number &&
            normalize(course.store_number) !== normalize(item.store_number)
          ) {
            continue;
          }
          matchedCourses.push({ section, course });
        }
      }

      if (!matchedCourses.length) {
        ProductDisplayManager._perfEnd(perfFilter, { result: 'no course match' });
        courseNotFoundFilters.push(item);
        console.warn('[CMRP restore] drop: no course match', {
          i: filterIndex,
          campusId,
          termId: item.selectedTermId,
          dept: item.selectedDepartment,
          course: item.selectedcourse,
          section: item.selectedsection,
          candidateSections: candidateSections.length,
        });
        return [];
      }

      let matchedSection = null;
      let matchedCourse = null;
      let filteredDepartment = null;
      const hasDivision = item.selectedDivision != null && item.selectedDivision !== '';
      let hadDepartmentMatch = false;

      for (let m = 0; m < matchedCourses.length; m++) {
        const { section, course } = matchedCourses[m];
        const departments = (
          ProductDisplayManager._deptsByCampusDeptId?.get(
            `${nCampus}|${normalize(course.campus_department_id)}`
          ) || []
        ).filter(
          department =>
            normalize(department.name) === normalize(item.selectedDepartment) ||
            normalize(department.abbreviation) ===
              normalize(item.selectedDepartment) ||
            normalize(department.campus_department_id) ===
              normalize(item.selectedDepartment) ||
            normalize(department.department_id || '') ===
              normalize(item.selectedDepartment)
        );

        if (!departments.length) continue;
        hadDepartmentMatch = true;

        // When division is selected, only accept a section whose department
        // belongs to a matching division (disambiguates same section names).
        if (hasDivision) {
          let matchedDivision = null;
          for (let d = 0; d < departments.length; d++) {
            const department = departments[d];
            const divisions = (
              ProductDisplayManager._divsByCampusDivId?.get(
                `${nCampus}|${normalize(department.campus_division_id)}`
              ) || []
            ).filter(
              division =>
                normalize(division.name) === normalize(item.selectedDivision) ||
                normalize(division.abbreviation) ===
                  normalize(item.selectedDivision)
            );
            if (divisions.length > 0) {
              matchedSection = section;
              matchedCourse = course;
              filteredDepartment = department;
              matchedDivision = divisions[0];
              item.selectedDivision = matchedDivision.name;
              break;
            }
          }
          if (matchedDivision) break;
          continue;
        }

        matchedSection = section;
        matchedCourse = course;
        filteredDepartment = departments[0];
        break;
      }

      if (!matchedSection || !matchedCourse || !filteredDepartment) {
        if (hasDivision && hadDepartmentMatch) {
          ProductDisplayManager._perfEnd(perfFilter, { result: 'no division match' });
          divisionNotFoundFilters.push(item);
          return [];
        }
        ProductDisplayManager._perfEnd(perfFilter, {
          result: hasDivision ? 'no department/division' : 'no department',
        });
        courseNotFoundFilters.push(item);
        return [];
      }

      item.selectedDepartment = filteredDepartment.name;

      item.selectedsectionGid = matchedSection?.gid || '';
      item.selectedsectionId = matchedSection?.section_id || '';
      item.adoption_status = matchedSection?.adoption_status || '';
      item.instructor_first_name = matchedSection?.instructor_first_name || '';
      item.instructor_last_name = matchedSection?.instructor_last_name || '';

      // @ts-ignore
      const sectionGid = matchedSection?.gid || '';
      const queryDataProd = "metafields.ddcs.ddcs_section:'" + sectionGid + "'";

      const newProducts = await this.fetchProducts(queryDataProd);
      perfStats.fetchCalls += 1;
      const taggedProducts = this._prepareFetchedProducts(newProducts, item);
      perfStats.productsFetched += taggedProducts.length;
      ProductDisplayManager._perfEnd(perfFilter, {
        path: 'section-first resolve+fetch',
        products: taggedProducts.length,
        sectionGid,
      });
      return taggedProducts;
      } catch (error) {
        console.error('Error resolving course materials for filter:', error);
        ProductDisplayManager._perfEnd(perfFilter, { result: 'filter error' });
        courseNotFoundFilters.push(item);
        return [];
      }
    });


    const productBatches = await Promise.all(promises);
    // @ts-ignore
    window.courseData.products_Materials = productBatches.flat();
    ProductDisplayManager._perfEnd(perfFetch, {
      fetchCalls: perfStats.fetchCalls,
      productsFetched: perfStats.productsFetched,
      sectionScans: perfStats.sectionScans,
      courseScans: perfStats.courseScans,
      products_Materials:
        window.courseData?.products_Materials?.length ?? 0,
    });
    // @ts-ignore

    if (generation !== this._refreshGeneration) {
      return;
    }

    // Same not-found cards as URL deep-link restore when DDCS has no course/section/division.
    const allNotFoundFilters = courseNotFoundFilters.concat(divisionNotFoundFilters);
    if (
      allNotFoundFilters.length > 0 &&
      allNotFoundFilters.length === selectedFilters.length
    ) {
      this.hideEmptyState();
      productsContainer.innerHTML = '';
      productsContainer.classList.add('course-layout');
      await this.displayCoursesGroupedByCampus(
        [],
        [],
        null,
        allNotFoundFilters,
        selectedFilters,
      );
      this.hideLoadingState();
      if (
        window.courseMaterialsSidebar &&
        typeof window.courseMaterialsSidebar.syncBubblesFromFilters === 'function'
      ) {
        window.courseMaterialsSidebar.syncBubblesFromFilters(selectedFilters);
      }
      document.dispatchEvent(
        new CustomEvent('product-display:updated', { bubbles: true })
      );
      if (typeof window.syncCmrpProductsSectionShell === 'function') {
        window.syncCmrpProductsSectionShell();
      }
      ProductDisplayManager._perfEnd(perfTotal, {
        filters: perfStats.filters,
        result: 'course/division not found',
      });
      return;
    }

    const resolvedFilters = selectedFilters.filter(
      f => !courseNotFoundFilters.includes(f) && !divisionNotFoundFilters.includes(f)
    );
    
    // Check if any of the selected filters have adoption_status
    const perfAdoption = ProductDisplayManager._perfStart('adoption split');
    const adoptionCourses = await this.getAdoptionCourses(resolvedFilters);
    const coursesWithAdoption = adoptionCourses;
    const coursesWithoutAdoption = await this.getCoursesWithoutAdoption(resolvedFilters);
    ProductDisplayManager._perfEnd(perfAdoption, {
      withAdoption: coursesWithAdoption.length,
      withoutAdoption: coursesWithoutAdoption.length,
    });

    const perfRender = ProductDisplayManager._perfStart('render DOM');
    productsContainer.innerHTML = '';
    if (coursesWithAdoption.length > 0 || coursesWithoutAdoption.length > 0) {
      this.hideEmptyState();
      productsContainer.classList.add('course-layout');
      await this.displayCoursesGroupedByCampus(
        coursesWithoutAdoption,
        coursesWithAdoption,
        null,
        allNotFoundFilters,
        selectedFilters,
      );
    } else {
      this.hideEmptyState();

      // Process products by department
      const productsByDepartment =
        this.processProductsByDepartment(resolvedFilters);

      if (productsContainer) {
        productsContainer.innerHTML = '';
        productsContainer.classList.add('course-layout');
      }

      await this.displayCoursesGroupedByCampus(
        [],
        [],
        productsByDepartment,
        allNotFoundFilters,
        selectedFilters,
      );
    }
    ProductDisplayManager._perfEnd(perfRender);

    if (generation !== this._refreshGeneration) {
      return;
    }
    
    const perfChips = ProductDisplayManager._perfStart('generateDepartmentChips');
    await window.generateDepartmentChips?.();
    ProductDisplayManager._perfEnd(perfChips);
    
    if (generation !== this._refreshGeneration) {
      return;
    }
    
    // Hide loading state only after department chips are generated and displayed
    this.hideLoadingState();
    
    // Sync course bubbles in sidebar with the filters that were just rendered
    if (window.courseMaterialsSidebar && typeof window.courseMaterialsSidebar.syncBubblesFromFilters === 'function') {
      window.courseMaterialsSidebar.syncBubblesFromFilters(resolvedFilters);
    }
    
    document.dispatchEvent(new CustomEvent('product-display:updated', {
      bubbles: true
    }));

    ProductDisplayManager._perfEnd(perfTotal, {
      filters: perfStats.filters,
      sectionScans: perfStats.sectionScans,
      courseScans: perfStats.courseScans,
      fetchCalls: perfStats.fetchCalls,
      productsFetched: perfStats.productsFetched,
    });
  }

  /**
   * Show the same gray "unable to find the specified course" card used by URL restore.
   * @param {any[]} failedFilters
   * @param {{ replace?: boolean, kind?: 'course' | 'division' }} [options]
   * @returns {boolean}
   */
  showCourseNotFoundErrorCards(failedFilters, options = {}) {
    if (!Array.isArray(failedFilters) || failedFilters.length === 0) return false;

    // @ts-ignore
    const urlManager = window.courseMaterialsPageInstance?.components?.urlManager;
    if (
      !urlManager ||
      typeof urlManager.buildUrlParamNotFoundCourseContext !== 'function'
    ) {
      return false;
    }

    const replace = options.replace !== false;
    const message = 'We are unable to find the specified course.';
    const campusInfoFor = failed => ({
      campus: failed?.selectedCampus || '',
      term: failed?.selectedTerm || '',
    });
    const contexts = failedFilters.map(failed =>
      urlManager.buildUrlParamNotFoundCourseContext(failed, campusInfoFor(failed))
    );

    if (replace) {
      if (typeof urlManager.showUrlParamNotFoundError !== 'function') return false;

      if (Array.isArray(urlManager.selectedFilters)) {
        urlManager.selectedFilters.length = 0;
      }
      // @ts-ignore
      const page = window.courseMaterialsPageInstance;
      if (Array.isArray(page?.selectedFilters)) {
        page.selectedFilters.length = 0;
      }
      urlManager.urlParamsToRestore = null;
      const courseList = document.getElementById('course-list');
      if (courseList) courseList.innerHTML = '';
      urlManager.showUrlParamNotFoundError('course', contexts);
      return true;
    }

    if (typeof urlManager.renderUrlParamNotFoundCourseCard !== 'function') {
      return false;
    }

    for (const context of contexts) {
      urlManager.renderUrlParamNotFoundCourseCard(message, context, { append: true });
    }
    if (typeof window.syncCmrpProductsSectionShell === 'function') {
      window.syncCmrpProductsSectionShell();
    }
    return true;
  }

  /**
   * Get courses with adoption status
   * @param {Array<any>} selectedFilters - Selected filters
   * @returns {Array<any>} Courses with adoption status
   * @private
   */
  async getAdoptionCourses(selectedFilters) {
    /** @type {Array<any>} */
    const adoptionCourses = [];
    for (const filter of selectedFilters) {
      if (
          filter &&
          filter.adoption_status &&
          filter.adoption_status !== '' &&
          (filter.adoption_status !== '500' ||
            !(window.courseData?.products_Materials || []).some(p =>
              ProductDisplayManager._productSectionMatchesFilter(p, filter)
            ))
      ) {
          let messageCode = 'No books required for this course.';
          switch (filter.adoption_status) {
            case '100': 
              messageCode = 'Course Materials have not been determined. Check back soon for updates.';
              break;
            case '200': 
            case '300':
            case '499':
            case '500':
              messageCode = 'Course Materials are being finalized. Check back soon for updates.';
              break;
            case '350': 
              messageCode = 'Free "Open Educational Resources" are required for this course. Please see your Instructor.';
              break;
            case '400':
              messageCode = ProductDisplayManager.getCmrpSamHiddenMessage();
              break;
            case '450': 
            case '460':
              messageCode = 'No bookstore supplied material. See your instructor for any custom material.';
              break;
            case '600': 
              messageCode = 'This class is cancelled. Please contact your campus with any questions.';
              break;
          
            default:
              break;
          }
          adoptionCourses.push({
              filter: filter,
              section: filter,
              courseCode: `${filter.selectedDepartment} ${filter.selectedcourse} Section ${filter.selectedsection}`,
              note: messageCode || 'No books required for this course.',
          });
      }
    }

    return adoptionCourses;
  }

  /**
   * Get courses without adoption status
   * @param {Array<any>} selectedFilters - Selected filters
   * @returns {Array<any>} Courses without adoption status
   * @private
   */
  async getCoursesWithoutAdoption(selectedFilters) {
    const adoptionWithoutCourses = [];
    for (const filter of selectedFilters) {
      if (
        !filter ||
        !filter.adoption_status ||
        filter.adoption_status === '' ||
        (filter.adoption_status === '500' &&
          (window.courseData?.products_Materials || []).some(p =>
            ProductDisplayManager._productSectionMatchesFilter(p, filter)
          ))
      ){
        adoptionWithoutCourses.push(filter);
      }
    }

    return adoptionWithoutCourses;
  }

  /**
   * Process products and group them by department
   * @param {Array<any>} selectedFilters - Selected filters
   * @returns {Record<string, any>} Products grouped by department
   * @private
   */
  processProductsByDepartment(selectedFilters) {
    const productsByDepartment = {};

    // @ts-ignore
    // @ts-ignore
    // @ts-ignore
    selectedFilters.forEach((filter, filterIndex) => {
      // @ts-ignore
      const filteredProducts = window.courseData.products_Materials.filter(
        product =>
          product &&
          ProductDisplayManager._productRowMatchesCourseSectionIds(
            product,
            filter
          )
      );

      // @ts-ignore
      filteredProducts.forEach(product => {
        const productsByDept = productsByDepartment;
        const courseKey = this.getCourseLineKey(filter);
        
        // @ts-ignore
        if (!productsByDept[courseKey]) {
          // @ts-ignore
          productsByDept[courseKey] = {
            products: [],
            courseInfo: {
              campus: filter.selectedCampus,
              term: filter.selectedTerm,
              division: filter.selectedDivision,
              course: filter.selectedcourse,
              section: filter.selectedsection,
              // @ts-ignore
              department: filter.selectedDepartment,
              termId: filter.selectedTermId,
              selectedsectionGid: filter.selectedsectionGid,
              selectedsectionId: filter.selectedsectionId,
              instructor_first_name: filter.instructor_first_name || '',
              instructor_last_name: filter.instructor_last_name || '',
            },
          };
        }
        // @ts-ignore
        productsByDept[courseKey].products.push(product);
      });
    });

    return productsByDepartment;
  }

  /**
   * Build bundle-aware display list: each entry is { parentProduct, childProducts }.
   * Parent = product with course_materials.is_package_flag === true.
   * Children = products whose course_materials.title_id is in the parent's course_materials.package_items.
   * When unifiedViewCMRP is on, print/digital counterparts merge only among those
   * package children — products not listed in package_items are never pulled in.
   * Child products are only shown under their parent (hidden from the main group).
   * Row order matches the input list so each bundle is one slot in the Required/Recommended sort.
   * Child cards inside a bundle are sorted Required → Recommended → Suggested, then by title.
   * @param {Array<any>} products - Flat list of products
   * @returns {Array<{ parentProduct: any, childProducts: any[] }>} Display list
   * @private
   */
  buildBundleDisplayList(products) {
    if (!Array.isArray(products) || products.length === 0) {
      return [];
    }

    const unifiedEnabled = !!window.shopifySettings?.unifiedViewCMRP;
    const getCm = (p) => p?.metafields?.course_materials || p?.metafields?.course_materials?.value;
    const isPackageParent = (p) => {
      const cm = getCm(p);
      const v = cm?.is_package_flag ?? cm?.is_package_flag?.value;
      return v === true || String(v).toLowerCase() === 'true';
    };
    const getPackageItems = (p) => {
      const cm = getCm(p);
      const raw = cm?.package_items ?? cm?.package_items?.value;
      if (Array.isArray(raw)) return raw;
      if (raw == null) return [];
      if (typeof raw === 'string') {
        try {
          const parsed = JSON.parse(raw);
          return Array.isArray(parsed) ? parsed : [];
        } catch (_) {
          return [];
        }
      }
      return [];
    };
    const getTitleId = (p) => {
      const cm = getCm(p);
      const v = cm?.title_id ?? cm?.title_id?.value;
      return v == null ? '' : String(v);
    };
    const childrenForParent = (parent) => {
      const packageItems = getPackageItems(parent);
      return products.filter((c) => {
        if (c === parent) return false;
        const tid = getTitleId(c);
        return tid !== '' && packageItems.some((item) => String(item) === tid);
      });
    };

    const parents = products.filter((p) => isPackageParent(p));
    const childrenByParent = new Map();
    const seenAsChild = new Set();
    parents.forEach((parent) => {
      const titleIdChildren = childrenForParent(parent);
      titleIdChildren.forEach((child) => seenAsChild.add(child));
      const childProducts = ProductDisplayManager._sortProductsByMaterialThenTitle(
        unifiedEnabled
          ? ProductDisplayManager._mergeUnifiedCmrpProducts(titleIdChildren)
          : titleIdChildren
      );
      childrenByParent.set(parent, childProducts);
    });

    /** @type {Array<{ parentProduct: any, childProducts: any[] }>} */
    const rows = [];
    products.forEach((p) => {
      if (isPackageParent(p)) {
        rows.push({
          parentProduct: p,
          childProducts: childrenByParent.get(p) || [],
        });
        return;
      }
      if (seenAsChild.has(p)) return;
      rows.push({ parentProduct: p, childProducts: [] });
    });

    if (!unifiedEnabled) return rows;

    const standaloneProducts = rows
      .filter((row) => row.childProducts.length === 0 && !isPackageParent(row.parentProduct))
      .map((row) => row.parentProduct);
    const mergedStandalone = new Set(
      ProductDisplayManager._mergeUnifiedCmrpProducts(standaloneProducts)
    );
    return rows.filter(
      (row) =>
        row.childProducts.length > 0 ||
        isPackageParent(row.parentProduct) ||
        mergedStandalone.has(row.parentProduct)
    );
  }

  /**
   * Check if product has print rental options (RENT, RENT NEW, RENT USED).
   * Digital rentals alone do not count — the semester-rent banner is print-only.
   * @param {any} product - Product object
   * @returns {boolean} True if has print rental options
   */
  hasRentalOptions(product) {
    if (!product.variants) return false;
    // @ts-ignore
    return product.variants.some(variant => {
      const option1 = variant.option1?.toLowerCase() || '';
      const option2 = variant.option2?.toLowerCase() || '';
      return option1.includes('print') && option2.includes('rent');
    });
  }

  /**
   * Get the lowest price for a specific format
   * @param {any} product - Product object
   * @param {string} format - Format to filter by (Digital, Print)
   * @returns {string} Formatted price string
   */
  getLowestPriceForFormat(product, format) {
    if (!product.variants) return 'From $0.00';
    
    // @ts-ignore
    const formatVariants = product.variants.filter(variant => {
      const option1 = variant.option1?.toLowerCase() || '';
      return option1.includes(format.toLowerCase());
    });
    
    if (formatVariants.length === 0) return 'From $0.00';
    
    const prices = formatVariants
      // @ts-ignore
      .filter(variant => variant.available)
      // @ts-ignore
      .map(variant => parseFloat(variant.price))
      // @ts-ignore
      .filter(price => !isNaN(price));
    
    if (prices.length === 0) return 'From $0.00';
    
    const lowestPrice = Math.min(...prices);
    return `From $${lowestPrice.toFixed(2)}`;
  }

  /**
   * True when product has ≥1 purchasable variant for the given format (Print/Digital).
   * Used for Unified View default-tab focus.
   * @param {any} product
   * @param {string} format
   * @returns {boolean}
   */
  hasAvailableFormatVariant(product, format) {
    if (!product?.variants || !format) return false;
    const needle = String(format).toLowerCase();
    return product.variants.some(variant => {
      const option1 = variant?.option1?.toLowerCase() || '';
      if (!option1.includes(needle)) return false;
      if (variant.available === false || variant.available === 'false') return false;
      if (variant.available === true || variant.available === 'true') return true;
      if (variant.inventory_policy === 'continue') return true;
      return Boolean(variant.available);
    });
  }

  /**
   * Stable key for grouping filters by campus + term.
   * @param {{ selectedCampus?: string, selectedTerm?: string, selectedTermId?: string }} filter
   * @returns {string}
   */
  getCampusTermGroupKey(filter) {
    const campus = String(filter?.selectedCampus || '')
      .trim()
      .toLowerCase();
    const termId = String(filter?.selectedTermId || filter?.selectedTerm || '')
      .trim()
      .toLowerCase();
    return `${campus}||${termId}`;
  }

  /**
   * Whether a campus|term group has actual book/product rows (not only
   * adoption / "unable to find course" status cards).
   * @param {{ normals?: any[], adoptions?: any[], notFound?: any[] }|null|undefined} group
   * @returns {boolean}
   */
  campusGroupHasBookMaterials(group) {
    if (!group || !Array.isArray(group.normals) || group.normals.length === 0) {
      return false;
    }
    const products = window.courseData?.products_Materials || [];
    return group.normals.some(item => {
      if (!item) return false;
      if (item.__sectionData) {
        return (
          Array.isArray(item.__sectionData.products) &&
          item.__sectionData.products.length > 0
        );
      }
      return products.some(
        p =>
          p &&
          ProductDisplayManager._productRowMatchesCourseSectionIds(p, item)
      );
    });
  }

  /**
   * Course-line key including campus/term so same dept/course/section on different
   * campuses do not collide.
   * @param {{ selectedCampus?: string, selectedTermId?: string, selectedTerm?: string, selectedDivision?: string, selectedDepartment?: string, selectedcourse?: string, selectedsection?: string }} filter
   * @returns {string}
   */
  getCourseLineKey(filter) {
    return [
      filter?.selectedCampus || '',
      filter?.selectedTermId || filter?.selectedTerm || '',
      filter?.selectedDivision || '',
      filter?.selectedDepartment || '',
      filter?.selectedcourse || '',
      filter?.selectedsection || '',
    ].join('|');
  }

  /**
   * Build campus|term title for a filter (always include campus when multi-group).
   * @param {{ selectedCampus?: string, selectedTerm?: string, selectedTermId?: string }} filter
   * @param {boolean} [forceCampus=false]
   * @returns {string}
   */
  formatFilterCampusTermTitle(filter, forceCampus = false) {
    const campus = String(filter?.selectedCampus || '').trim();
    const termId = String(filter?.selectedTermId || '').trim();
    const term =
      window.courseData?.ddcs_term?.find(t => t.term_id === termId)?.name ||
      filter?.selectedTerm ||
      '';
    const campusEntities = window.courseData?.ddcs_campus_entity;
    if (forceCampus) {
      if (campus && term) return `${campus} | ${term}`;
      return campus || term || '';
    }
    if (window.CourseMaterialsUtils?.formatCampusTermTitle) {
      return CourseMaterialsUtils.formatCampusTermTitle(
        campus,
        term,
        campusEntities,
      );
    }
    return campus && term ? `${campus} | ${term}` : campus || term || '';
  }

  /**
   * Create a campus/term group wrapper with an inline header.
   * For the first multi group, pass `isPrimary: true` so the inline title is
   * visually hidden on screen (top #campus-term-info already shows it) but
   * remains available for print.
   * @param {{ selectedCampus?: string, selectedTerm?: string, selectedTermId?: string }} filter
   * @param {{ isPrimary?: boolean }} [options]
   * @returns {{ wrap: HTMLElement, body: HTMLElement }}
   */
  createCampusMaterialsGroup(filter, options = {}) {
    const isPrimary = options.isPrimary === true;
    const wrap = document.createElement('div');
    wrap.className = 'campus-materials-group';
    if (isPrimary) wrap.classList.add('campus-materials-group--first');
    const campus = String(filter?.selectedCampus || '').trim();
    const termId = String(filter?.selectedTermId || '').trim();
    const term =
      window.courseData?.ddcs_term?.find(t => t.term_id === termId)?.name ||
      filter?.selectedTerm ||
      '';
    wrap.dataset.campus = campus;
    wrap.dataset.termId = termId;
    wrap.dataset.term = term;

    const header = document.createElement('div');
    header.className = 'campus-term-bar campus-term-bar--inline';
    if (isPrimary) {
      header.classList.add('campus-term-bar--inline-primary');
    }
    header.innerHTML = `
      <div class="campus-term-content">
        <div class="campus-term-left">
          <div class="campus-term-title campus-term-heading"></div>
        </div>
      </div>
    `;
    const titleEl = header.querySelector('.campus-term-title');
    if (titleEl && window.CourseMaterialsUtils?.setResultsCampusTermHeading) {
      CourseMaterialsUtils.setResultsCampusTermHeading(titleEl, campus, term, {
        forceCampus: true,
      });
    } else if (titleEl) {
      titleEl.textContent = this.formatFilterCampusTermTitle(filter, true);
    }
    wrap.appendChild(header);

    const body = document.createElement('div');
    body.className = 'campus-materials-group__courses';
    wrap.appendChild(body);
    return { wrap, body };
  }

  /**
   * Render gray "unable to find the specified course" cards into a campus group.
   * @param {Array<any>} notFoundFilters
   * @param {HTMLElement|null} [parentContainer=null]
   */
  displayUrlParamNotFoundCards(notFoundFilters, parentContainer = null) {
    if (!Array.isArray(notFoundFilters) || notFoundFilters.length === 0) return;
    const urlManager =
      window.courseMaterialsPageInstance?.components?.urlManager;
    if (
      !urlManager ||
      typeof urlManager.renderUrlParamNotFoundCourseCard !== 'function' ||
      typeof urlManager.buildUrlParamNotFoundCourseContext !== 'function'
    ) {
      return;
    }
    const messageFor = failed =>
      typeof urlManager.urlParamNotFoundMessage === 'function'
        ? urlManager.urlParamNotFoundMessage(
            failed?._urlParamNotFoundKind || 'course'
          )
        : 'We are unable to find the specified course.';
    for (const failed of notFoundFilters) {
      const context = urlManager.buildUrlParamNotFoundCourseContext(failed, {
        campus: failed?.selectedCampus || '',
        term: failed?.selectedTerm || '',
      });
      urlManager.renderUrlParamNotFoundCourseCard(messageFor(failed), context, {
        append: true,
        parent: parentContainer,
      });
    }
  }

  /**
   * Sync top bar for multi/single campus groups.
   * Multi: first campus|term stays in #campus-term-info beside Print List;
   * additional groups use inline headers only.
   * @param {boolean} multiCampus
   * @param {{ selectedCampus?: string, selectedTerm?: string, selectedTermId?: string }|null} [firstFilter]
   * @returns {void}
   */
  syncTopCampusTermBarForGroups(multiCampus, firstFilter = null) {
    const campusTermBar = document.getElementById('campus-term-info');
    const campusDisplay = document.getElementById('selected-campus-display');
    if (!campusTermBar) return;

    const campusTermLeft = campusTermBar.querySelector('.campus-term-left');
    campusTermBar.classList.toggle('campus-term-bar--multi', multiCampus);
    if (campusTermLeft) campusTermLeft.style.display = '';

    if (multiCampus && firstFilter && campusDisplay) {
      const campus = String(firstFilter?.selectedCampus || '').trim();
      const termId = String(firstFilter?.selectedTermId || '').trim();
      const term =
        window.courseData?.ddcs_term?.find(t => t.term_id === termId)?.name ||
        firstFilter?.selectedTerm ||
        '';
      if (campus) {
        if (window.CourseMaterialsUtils?.setResultsCampusTermHeading) {
          CourseMaterialsUtils.setResultsCampusTermHeading(
            campusDisplay,
            campus,
            term,
            { forceCampus: true },
          );
        } else {
          campusDisplay.textContent = this.formatFilterCampusTermTitle(
            firstFilter,
            true,
          );
        }
        campusTermBar.style.display = 'flex';
      }
    }
  }

  /**
   * Render courses grouped by campus+term. When multiple campuses are present,
   * groups with book materials are listed first and status-only groups last
   * (same idea as single-term: books above adoption / not-found cards).
   * The first title lives in the top bar; later groups get inline headers.
   * @param {Array<any>} coursesWithoutAdoption
   * @param {Array<any>} coursesWithAdoption
   * @param {Record<string, any>|null} [prebuiltProductsByCourse=null]
   * @param {Array<any>|null} [notFoundFilters=null]
   * @param {Array<any>|null} [originalFilters=null]
   */
  async displayCoursesGroupedByCampus(
    coursesWithoutAdoption,
    coursesWithAdoption,
    prebuiltProductsByCourse = null,
    notFoundFilters = null,
    originalFilters = null,
  ) {
    const productsContainer = this.getProductsContainer();
    if (!productsContainer) return;

    /** @type {string[]} */
    const groupOrder = [];
    /** @type {Map<string, { filter: any, normals: any[], adoptions: any[], notFound: any[] }>} */
    const groups = new Map();

    const ensureGroup = filter => {
      const key = this.getCampusTermGroupKey(filter);
      if (!groups.has(key)) {
        groups.set(key, { filter, normals: [], adoptions: [], notFound: [] });
        groupOrder.push(key);
      }
      return groups.get(key);
    };

    if (Array.isArray(originalFilters)) {
      for (const filter of originalFilters) {
        if (filter) ensureGroup(filter);
      }
    }

    for (const filter of coursesWithoutAdoption || []) {
      if (!filter) continue;
      ensureGroup(filter).normals.push(filter);
    }
    for (const adoptionCourse of coursesWithAdoption || []) {
      const filter = adoptionCourse?.filter;
      if (!filter) continue;
      ensureGroup(filter).adoptions.push(adoptionCourse);
    }
    for (const filter of notFoundFilters || []) {
      if (!filter) continue;
      ensureGroup(filter).notFound.push(filter);
    }

    // Fallback path: pre-built department map without adoption split
    if (
      groupOrder.length === 0 &&
      prebuiltProductsByCourse &&
      typeof prebuiltProductsByCourse === 'object'
    ) {
      for (const courseKey of Object.keys(prebuiltProductsByCourse)) {
        const sectionData = prebuiltProductsByCourse[courseKey];
        const pseudoFilter = {
          selectedCampus: sectionData?.courseInfo?.campus || '',
          selectedTerm: sectionData?.courseInfo?.term || '',
          selectedTermId: sectionData?.courseInfo?.termId || '',
        };
        const group = ensureGroup(pseudoFilter);
        group.normals.push({
          __prebuiltKey: courseKey,
          __sectionData: sectionData,
        });
      }
    }

    // Only split into per-campus groups when there are 2+ distinct campus|term keys.
    // Same as single-term course order: groups with books first, status-only last
    // (adoption / course-not-found). Relative URL order is kept within each bucket.
    // First campus|term title stays in the top bar beside Print List; later groups
    // get inline headers (so the first is not duplicated).
    const visibleKeys = groupOrder.filter(key => {
      const g = groups.get(key);
      return (
        g &&
        (g.normals.length > 0 ||
          g.adoptions.length > 0 ||
          (g.notFound && g.notFound.length > 0))
      );
    });
    visibleKeys.sort((a, b) => {
      const aHas = this.campusGroupHasBookMaterials(groups.get(a)) ? 0 : 1;
      const bHas = this.campusGroupHasBookMaterials(groups.get(b)) ? 0 : 1;
      return aHas - bHas;
    });
    const groupHasKnownCampus = group =>
      Boolean(String(group?.filter?.selectedCampus || '').trim());
    const multiCampus = visibleKeys.length > 1;
    const firstKnownKey = visibleKeys.find(key =>
      groupHasKnownCampus(groups.get(key)),
    );
    const firstGroupFilter = multiCampus
      ? groups.get(firstKnownKey || visibleKeys[0])?.filter || null
      : null;
    this._lastCampusGroupCount = visibleKeys.length;
    productsContainer.classList.toggle('has-multi-campus-groups', multiCampus);

    this.syncTopCampusTermBarForGroups(multiCampus, firstGroupFilter);

    let wrappedIndex = 0;
    for (let groupIndex = 0; groupIndex < visibleKeys.length; groupIndex++) {
      const key = visibleKeys[groupIndex];
      const group = groups.get(key);
      if (!group) continue;

      let appendTarget = productsContainer;
      if (multiCampus && groupHasKnownCampus(group)) {
        const { wrap, body } = this.createCampusMaterialsGroup(group.filter, {
          // First known-campus title is in #campus-term-info — hide duplicate on screen.
          isPrimary: wrappedIndex === 0,
        });
        productsContainer.appendChild(wrap);
        appendTarget = body;
        wrappedIndex += 1;
      }

      const prebuiltNormals = group.normals.filter(n => n && n.__prebuiltKey);
      const regularNormals = group.normals.filter(n => n && !n.__prebuiltKey);

      if (regularNormals.length > 0) {
        await this.displayNormalProductSections(regularNormals, appendTarget);
      }
      for (const item of prebuiltNormals) {
        await this.displayDepartmentSection(
          item.__prebuiltKey,
          item.__sectionData,
          appendTarget,
        );
      }
      if (group.adoptions.length > 0) {
        this.displayAdoptionStatusCards(group.adoptions, appendTarget);
      }
      if (group.notFound && group.notFound.length > 0) {
        this.displayUrlParamNotFoundCards(group.notFound, appendTarget);
      }
    }

    this.syncTopCampusTermBarForGroups(multiCampus, firstGroupFilter);
    if (typeof window.updateCampusTermDisplay === 'function') {
      window.updateCampusTermDisplay();
    }
  }

  /**
   * Display adoption status cards for courses
   * @param {Array<any>} adoptionCourses - Courses with adoption status
   * @param {HTMLElement|null} [parentContainer=null]
   */
  displayAdoptionStatusCards(adoptionCourses, parentContainer = null) {
    const productsContainer = parentContainer || this.getProductsContainer();
    if (!productsContainer) return;

    ProductDisplayManager._ensureDdcsIndexes();

    // Create individual sections for each course (not grouped by department)
    adoptionCourses.forEach(adoptionCourse => {
      try {
      const { filter, section, courseCode, note } = adoptionCourse;
      const divisionName = String(filter.selectedDivision || '').trim();
      const departmentName = String(filter.selectedDepartment || '').trim();
      const scrollLabel = divisionName || departmentName;

      // Create individual course section
      const courseSection = document.createElement('div');
      courseSection.className = 'course-section';
      courseSection.dataset.departmentFilter = departmentName.toLowerCase();
      courseSection.dataset.campus = filter.selectedCampus || '';
      courseSection.dataset.termId = filter.selectedTermId || '';
      if (divisionName) {
        courseSection.dataset.divisionFilter = divisionName.toLowerCase();
      }
      if (scrollLabel) {
        courseSection.dataset.scrollGroup = scrollLabel.toLowerCase();
        courseSection.innerHTML = `
                  <div class="department-title">
                      <span class="department-name">${scrollLabel}</span>
                  </div>
              `;
      }

      // Add adoption card for this specific course
      const statusInfo = this.getAdoptionStatusInfo(note);
      const adoptionCard = document.createElement('div');
      adoptionCard.className = 'adoption-status-section';
      adoptionCard.innerHTML = this.createAdoptionCardHTML(
        filter,
        section,
        courseCode,
        note,
        statusInfo
      );

      const sectionId = String(filter.selectedsectionId ?? '');
      const { sectionNote, courseNote } =
        ProductDisplayManager._resolvePrintableSectionCourseNotes(
          sectionId,
          ProductDisplayManager._sectionsById?.get(sectionId)
        );
      const notesEl = ProductDisplayManager._buildSectionCourseNotesElement(
        sectionNote,
        courseNote
      );
      if (notesEl) {
        const card = adoptionCard.querySelector('.course-card');
        const message = card?.querySelector('.adoption-message-content');
        if (message?.parentNode) {
          message.parentNode.insertBefore(notesEl, message);
        } else if (card) {
          card.appendChild(notesEl);
        }
      }

      courseSection.appendChild(adoptionCard);
      productsContainer.appendChild(courseSection);
      } catch (error) {
        console.error('[CMRP restore] adoption card render failed', {
          dept: adoptionCourse?.filter?.selectedDepartment,
          course: adoptionCourse?.filter?.selectedcourse,
          section: adoptionCourse?.filter?.selectedsection,
          error,
        });
      }
    });
  }

  /**
   * Display normal product sections (without adoption status)
   * @param {Array<any>} coursesWithoutAdoption - Courses without adoption status
   * @param {HTMLElement|null} [parentContainer=null]
   */
  async displayNormalProductSections(
    coursesWithoutAdoption,
    parentContainer = null,
  ) {
    const productsByDepartment = {};
    /** @type {Map<string, Set<string>>} */
    const seenProductIdsByCourse = new Map();

    // @ts-ignore
    // @ts-ignore
    // @ts-ignore
    coursesWithoutAdoption.forEach((filter, index) => {
      // @ts-ignore
      const filteredProducts = window.courseData.products_Materials.filter(
        product =>
          product &&
          ProductDisplayManager._productRowMatchesCourseSectionIds(
            product,
            filter
          )
      );
      // @ts-ignore
      filteredProducts.forEach(product => {
        const courseKey = this.getCourseLineKey(filter);
        /** @type {Record<string, any>} */
        const productsByDept = productsByDepartment;
        if (!productsByDept[courseKey]) {
          productsByDept[courseKey] = {
            products: [],
            courseInfo: {
              campus: filter.selectedCampus,
              term: filter.selectedTerm,
              division: filter.selectedDivision,
              course: filter.selectedcourse,
              section: filter.selectedsection,
              department: filter.selectedDepartment,
              termId: filter.selectedTermId,
              selectedsectionGid: filter.selectedsectionGid,
              selectedsectionId: filter.selectedsectionId,
              instructor_first_name: filter.instructor_first_name || '',
              instructor_last_name: filter.instructor_last_name || '',
            },
          };
        }
        const pid = ProductDisplayManager._productDedupeId(product);
        if (pid) {
          if (!seenProductIdsByCourse.has(courseKey)) {
            seenProductIdsByCourse.set(courseKey, new Set());
          }
          const seen = seenProductIdsByCourse.get(courseKey);
          if (seen.has(pid)) {
            return;
          }
          seen.add(pid);
        }
        productsByDept[courseKey].products.push(product);
      });
    });

    // Display sections with products
    const departments = Object.keys(productsByDepartment);
    // Use for...of para permitir o uso de await
    for (const department of departments) {
        /** @type {Record<string, any>} */
        const productsByDept = productsByDepartment;
        await this.displayDepartmentSection(
          department,
          productsByDept[department],
          parentContainer,
        );
    }

    // Display empty sections for courses without products
    for (const filter of coursesWithoutAdoption) {
        const courseKey = this.getCourseLineKey(filter);
        /** @type {Record<string, any>} */
        const productsByDept = productsByDepartment;
        if (!productsByDept[courseKey]) {
          await this.displayDepartmentSection(
            courseKey,
            {
              products: [],
              courseInfo: {
                  campus: filter.selectedCampus,
                  term: filter.selectedTerm,
                  division: filter.selectedDivision,
                  course: filter.selectedcourse,
                  section: filter.selectedsection,
                  department: filter.selectedDepartment,
                  termId: filter.selectedTermId,
                  selectedsectionGid: filter.selectedsectionGid,
                  selectedsectionId: filter.selectedsectionId,
                  instructor_first_name: filter.instructor_first_name || '',
                  instructor_last_name: filter.instructor_last_name || '',
              },
            },
            parentContainer,
          );
        }
    }
  }

  /**
   * Display a department section with its products
   * @param {string} department - Department key
   * @param {any} sectionData - Section data with products and course info
   * @param {HTMLElement|null} [parentContainer=null]
   */
  async displayDepartmentSection(
    department,
    sectionData,
    parentContainer = null,
  ) {
    const perfDept = ProductDisplayManager._perfStart(
      `displayDepartmentSection: ${department}`
    );
    const productsContainer = parentContainer || this.getProductsContainer();
    if (!productsContainer) {
      ProductDisplayManager._perfEnd(perfDept, { result: 'no container' });
      return;
    }
    ProductDisplayManager._ensureDdcsIndexes();
    // Find the section data to get instructor information (normalize IDs to string for comparison)
    const selectedsectionIdRaw = sectionData.courseInfo.selectedsectionId;
    const sectionIdKey = String(selectedsectionIdRaw ?? '');
    let sectionInfo = ProductDisplayManager._sectionsById?.get(sectionIdKey);
    if (!sectionInfo && sectionIdKey && window.courseData?.ddcs_section?.length) {
      sectionInfo = window.courseData.ddcs_section.find(
        s => String(s?.section_id ?? s?.section_d ?? '') === sectionIdKey
      );
    }
    // Get instructor name from first and last name fields
    const instructorName = sectionInfo
      ? `${sectionInfo.instructor_first_name || ''} ${
          sectionInfo.instructor_last_name || ''
        }`.trim() || 'TBA'
      : 'TBA';

    // Get section display name or fall back to section name
    const sectionNumber =
      sectionInfo?.display_name || sectionData.courseInfo.section;

    const sectionId = String(selectedsectionIdRaw ?? '');

    let ddcsCourseFromProxy = [];
    try {
      const fromPage = window.courseData?.ddcs_course;
      if (Array.isArray(fromPage) && fromPage.length > 0) {
        ddcsCourseFromProxy = fromPage;
      } else {
        const query = sectionId ? `fields.section_d:'${sectionId}'` : '';
        const promiseKey = sectionId ? `ddcs_course_${sectionId}` : 'ddcs_course';
        if (!this._ddcsCourseProxyPromises) this._ddcsCourseProxyPromises = {};
        if (!this._ddcsCourseProxyPromises[promiseKey]) {
          this._ddcsCourseProxyPromises[promiseKey] = this.fetchMetaobjects('ddcs_course', query);
        }
        ddcsCourseFromProxy = await this._ddcsCourseProxyPromises[promiseKey];
      }
    } catch {
      ddcsCourseFromProxy = window.courseData?.ddcs_course ?? [];
    }

    const { sectionNote, courseNote } =
      ProductDisplayManager._resolvePrintableSectionCourseNotes(
        sectionId,
        sectionInfo,
        ddcsCourseFromProxy
      );

    const section = document.createElement('div');
    section.className = 'course-section';

    const departmentTitle = document.createElement('div');
    departmentTitle.className = 'department-title';

    // Extract department name from courseKey or use sectionData
    const departmentName = sectionData.courseInfo
      ? sectionData.courseInfo.department
      : department.split('-')[0];
    const divisionName = String(sectionData.courseInfo?.division || '').trim();
    const departmentLabel = String(departmentName || '').trim();
    section.dataset.departmentFilter = departmentLabel.toLowerCase();
    if (divisionName) {
      section.dataset.divisionFilter = divisionName.toLowerCase();
      section.dataset.scrollGroup = divisionName.toLowerCase();
    } else {
      section.dataset.scrollGroup = departmentLabel.toLowerCase();
    }

    // Count sections for this department (indexed — O(1) vs scanning 15k+ sections per card)
    const departmentSectionsCount =
      ProductDisplayManager.getDepartmentSectionCount(departmentName);

    const samSet = ProductDisplayManager.getCmrpSamSkuSet();
    if (samSet.size > 0) {
      const orig = sectionData.products;
      const allSam =
        Array.isArray(orig) &&
        orig.length > 0 &&
        orig.every(p => ProductDisplayManager.productHasSamSku(p, samSet));
      const mixed =
        Array.isArray(orig) &&
        orig.some(p => ProductDisplayManager.productHasSamSku(p, samSet)) &&
        !allSam;
      if (allSam) {
        sectionData.products = [];
        sectionData._cmrpSamOnlyAllSam = true;
      } else if (mixed) {
        sectionData.products = orig.filter(
          p => !ProductDisplayManager.productHasSamSku(p, samSet)
        );
        sectionData._cmrpSamOnlyAllSam = false;
      } else {
        sectionData._cmrpSamOnlyAllSam = false;
      }
    } else {
      sectionData._cmrpSamOnlyAllSam = false;
    }

    const sectionIdNeedle = String(
      sectionData.courseInfo.selectedsectionId ?? ''
    ).trim();
    const filteredItems = sectionData.products.filter(item =>
      ProductDisplayManager._productRowMatchesCourseSectionIds(
        item,
        sectionData.courseInfo
      )
    );
    const filteredProducts = filteredItems.reduce((acc, product) => {
      
      if (product.metafields.book_type_ind === "CEB") {
        const printRelationshipISBN = product.metafields.print_relationship_isbn;
        if (printRelationshipISBN && printRelationshipISBN !== "") {
          const isbnArray = JSON.parse(printRelationshipISBN);
          const shouldRemove = isbnArray.some(isbn => {
              return filteredItems.some(item => 
                  item.metafields.isbn_ean === isbn &&
                  item.material_type === product.material_type 
              );
          });
          if (shouldRemove) {
            const variantsToAdd = product.variants || [];
            const parentProduct = filteredItems.find(item => 
                item.metafields.book_type_ind === "TXT" && 
                item.material_type === product.material_type && 
                isbnArray.some(isbn => item.metafields.isbn_ean === isbn) 
            );
            if (parentProduct) {
              ProductDisplayManager._stampVariantsSourceIsbn(
                variantsToAdd,
                product
              );
              parentProduct.variants = [...(parentProduct.variants || []), ...variantsToAdd];
              ProductDisplayManager._copyMergedVariantDigitalStatus(
                parentProduct,
                product,
                variantsToAdd
              );
            }
            return acc; 
          }
        }
      }
      acc.push(product);
      return acc;
    }, []);
    // Section heading: division when present, otherwise department (scroll-chip targets).
    const sectionHeadingLabel = divisionName || departmentLabel;
    if (sectionHeadingLabel) {
      departmentTitle.innerHTML = `
              <span class="department-name">${sectionHeadingLabel}</span>
          `;
      section.appendChild(departmentTitle);
    }

    const courseCard = document.createElement('div');
    courseCard.className = 'course-card';
    // Expose course context on the card so add-to-cart can read it (checkbox has no course data).
    courseCard.dataset.campus = sectionData.courseInfo.campus ?? '';
    courseCard.dataset.term = sectionData.courseInfo.term ?? '';
    courseCard.dataset.division = sectionData.courseInfo.division ?? '';
    courseCard.dataset.department = sectionData.courseInfo.department ?? '';
    courseCard.dataset.course = sectionData.courseInfo.course ?? '';
    courseCard.dataset.section = sectionData.courseInfo.section ?? '';
    courseCard.dataset.termId = sectionData.courseInfo.termId ?? '';
    courseCard.dataset.selectedSectionId =
      sectionData.courseInfo.selectedsectionId ?? '';

    const headerSection = document.createElement('div');
    headerSection.className = 'course-header';
    // Render using course header template (mimics snippets/course-header.liquid)
    
    let requiredCount =
    filteredProducts.length > 0
        ? filteredProducts.filter(
            /** @param {any} p */ p => 
                ['Required'].includes(p.material_type)
          ).length
        : 0;
    const choiceMaterials = filteredProducts.filter(
      /** @param {any} p */ p => 
      ['Choice', 'Choice/Required', 'Choice/Recommended'].includes(p.material_type)
    );
    let choice_minimum = '0'
    if (choiceMaterials.length > 0) {
        // Prefer the section already resolved during refresh. `dev` re-looks this
        // up department-first; a miss returns or throws on [0] and aborts later cards.
        const fromResolved = sectionInfo?.choice_minimum;
        if (fromResolved != null && fromResolved !== '') {
          choice_minimum = String(fromResolved);
          requiredCount = requiredCount + (parseInt(choice_minimum, 10) || 0);
        } else {
        try {
        // @ts-ignore
        let filteredDepartments;
        let filteredDivision;
        const normalize = v => ProductDisplayManager._normalizeCmrpCompareKey(v);
        const campusMatchChoice = (window.courseData.ddcs_campus_entity || []).find(
          campus => normalize(campus.name) === normalize(sectionData.courseInfo.campus),
        );
        let campusId =
          campusMatchChoice?.campus_entity_id ||
          window.courseMaterialsSidebar?.campus_entity_id;
        if(sectionData.courseInfo?.division && sectionData.courseInfo.division != ''){
          filteredDivision = window.courseData.ddcs_campus_division.filter(division => normalize(division.campus_entity_id) === normalize(campusId) && (normalize(division.name) === normalize(sectionData.courseInfo.division) || normalize(division.abbreviation) === normalize(sectionData.courseInfo.division)));
            for(var div=0;div<filteredDivision.length;div++){
              filteredDepartments = window.courseData.ddcs_campus_department.filter(department => normalize(department.campus_entity_id) === normalize(campusId) && normalize(department.campus_division_id) === normalize(filteredDivision[div].campus_division_id) && (normalize(department.name) === normalize(sectionData.courseInfo.department) || normalize(department.abbreviation) === normalize(sectionData.courseInfo.department)));
              if(filteredDepartments.length > 0){
                break;
              }
            }
        }else{
          filteredDepartments = window.courseData.ddcs_campus_department.filter(department => normalize(department.campus_entity_id) === normalize(campusId) && (normalize(department.name) === normalize(sectionData.courseInfo.department) || normalize(department.abbreviation) === normalize(sectionData.courseInfo.department)));
        }
        if (!filteredDepartments || filteredDepartments.length === 0) {
          filteredDepartments = window.courseData.ddcs_campus_department.filter(department => normalize(department.campus_entity_id) === normalize(campusId) && (normalize(department.name) === normalize(sectionData.courseInfo.department) || normalize(department.abbreviation) === normalize(sectionData.courseInfo.department)   ));
        }
        if (!filteredDepartments || filteredDepartments.length === 0) {
          console.warn('[CMRP restore] choice lookup miss (no department); rendering card', {
            dept: sectionData.courseInfo.department,
            course: sectionData.courseInfo.course,
            section: sectionData.courseInfo.section,
            division: sectionData.courseInfo.division,
          });
        } else {
        const filteredCourse = window.courseData.ddcs_course.filter(course => normalize(course.campus_entity_id) === normalize(campusId) && normalize(course.campus_department_id) === normalize(filteredDepartments[0].campus_department_id) && normalize(course.coursenumber) === normalize(sectionData.courseInfo.course));
        const filteredSection = window.courseData.ddcs_section.filter(section => normalize(section.campus_entity_id) === normalize(campusId) && normalize(section.course_id) === normalize(filteredCourse[0].course_id) && normalize(section.name) === normalize(sectionData.courseInfo.section)); 
        choice_minimum = filteredSection[0].choice_minimum
        requiredCount = requiredCount + parseInt(filteredSection[0].choice_minimum);
        }
        } catch (error) {
          console.warn('[CMRP restore] choice lookup threw; rendering card', {
            dept: sectionData.courseInfo.department,
            course: sectionData.courseInfo.course,
            section: sectionData.courseInfo.section,
            error,
          });
        }
        }
    }

    const sectionCourseNotes =
      ProductDisplayManager._buildSectionCourseNotesElement(
        sectionNote,
        courseNote
      );

    // Helper function to generate remove button HTML
    const getRemoveButtonHTML = () => `
      <button class="remove-btn" type="button" aria-label="Remove course" onclick="removeCourse(this)" 
              data-campus="${sectionData.courseInfo.campus ?? ''}" 
              data-term="${sectionData.courseInfo.term ?? ''}" 
              data-division="${sectionData.courseInfo.division ?? ''}" 
              data-department="${sectionData.courseInfo.department}" 
              data-course="${sectionData.courseInfo.course}" 
              data-section="${sectionData.courseInfo.section}"
              data-section-id="${sectionData.courseInfo.selectedsectionId ?? ''}"
              data-section-gid="${sectionData.courseInfo.selectedsectionGid ?? ''}">
          Remove
          <svg class="remove-icon" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true" focusable="false">
              <path d="M13.5 4.5L4.5 13.5M4.5 4.5L13.5 13.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
      </button>
    `;

    // Helper: normalize campus Included Program Name for display
    const formatAccessProgramName = (name) => {
      const trimmed = typeof name === 'string' ? name.trim() : '';
      if (!trimmed) return '';
      if (/^follett\s+access$/i.test(trimmed)) return 'Follett Access';
      return trimmed;
    };

    // Helper function to generate access program banner HTML
    const getAccessProgramBannerHTML = (sectionNote) => {
      const label = ProductDisplayManager.resolveAccessProgramLabel(
        formatAccessProgramName(sectionNote)
      );
      const bannerText = ProductDisplayManager.fillAccessProgramPlaceholderForHtml(
        ProductDisplayManager.getAccessProgramBannerTemplate(),
        label
      );
      const tooltipTitle = ProductDisplayManager.fillAccessProgramPlaceholderForHtml(
        ProductDisplayManager.getAccessProgramTitleTemplate(),
        label,
        true
      );
      const tooltipMessage = ProductDisplayManager.fillAccessProgramPlaceholderForHtml(
        ProductDisplayManager.getAccessProgramBodyTemplate(),
        label
      );

      // Shared decorative icon (label text provides the accessible name)
      const icon = `
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true" focusable="false">
          <path fill-rule="evenodd" clip-rule="evenodd" d="M8.03751 3.23346C7.81155 3.23346 7.61727 3.41091 7.61727 3.6432V3.9899H7.49033C7.1739 3.9899 6.8716 4.11407 6.64959 4.33328C6.42775 4.55234 6.30402 4.84839 6.30402 5.15606C6.30402 5.46373 6.42775 5.75978 6.64959 5.97884C6.8716 6.19806 7.1739 6.32223 7.49033 6.32223H7.61727V7.01562H6.72426C6.4983 7.01562 6.30402 7.19307 6.30402 7.42536C6.30402 7.65765 6.4983 7.83509 6.72426 7.83509H7.61727V8.18179C7.61727 8.41408 7.81155 8.59153 8.03751 8.59153C8.26348 8.59153 8.45775 8.41408 8.45775 8.18179V7.83509H8.5847C8.90113 7.83509 9.20342 7.71092 9.42543 7.4917C9.64728 7.27265 9.771 6.9766 9.771 6.66893C9.771 6.36126 9.64728 6.06521 9.42543 5.84615C9.20342 5.62693 8.90113 5.50276 8.5847 5.50276H8.45775V4.80936H9.13189C9.35786 4.80936 9.55213 4.63192 9.55213 4.39963C9.55213 4.16734 9.35786 3.9899 9.13189 3.9899H8.45775V3.6432C8.45775 3.41091 8.26348 3.23346 8.03751 3.23346ZM7.24768 4.90908C7.31298 4.84461 7.40037 4.80936 7.49033 4.80936H7.61727V5.50276H7.49033C7.40037 5.50276 7.31298 5.46752 7.24768 5.40304C7.18218 5.33836 7.1445 5.2496 7.1445 5.15606C7.1445 5.06252 7.18218 4.97376 7.24768 4.90908ZM8.45775 7.01562V6.32223H8.5847C8.67465 6.32223 8.76205 6.35747 8.82734 6.42195C8.89285 6.48663 8.93052 6.57539 8.93052 6.66893C8.93052 6.76247 8.89285 6.85123 8.82734 6.91591C8.76205 6.98038 8.67465 7.01562 8.5847 7.01562H8.45775Z" fill="#204A65"/>
          <path fill-rule="evenodd" clip-rule="evenodd" d="M9.61333 12.6985C9.61333 12.9741 9.39751 13.1976 9.13129 13.1976H6.85164C6.58542 13.1976 6.3696 12.9741 6.3696 12.6985C6.3696 12.4229 6.58542 12.1995 6.85164 12.1995H9.13129C9.39751 12.1995 9.61333 12.4229 9.61333 12.6985Z" fill="#204A65"/>
          <path fill-rule="evenodd" clip-rule="evenodd" d="M9.18567 9.08436L9.68398 8.7961C10.6952 8.21114 11.3719 7.12026 11.3719 5.8725C11.3719 4.00885 9.86114 2.49807 7.9975 2.49807C6.13385 2.49807 4.62307 4.00885 4.62307 5.8725C4.62307 7.12026 5.29978 8.21114 6.31101 8.7961L6.80932 9.08435V10.6094C6.80932 10.6619 6.85187 10.7044 6.90437 10.7044H9.09062C9.14312 10.7044 9.18567 10.6619 9.18567 10.6094V9.08436ZM10.1837 9.66004V10.6094C10.1837 11.2131 9.69433 11.7025 9.09062 11.7025H6.90437C6.30065 11.7025 5.81125 11.2131 5.81125 10.6094V9.66003C4.50431 8.90401 3.625 7.49094 3.625 5.8725C3.625 3.45763 5.58263 1.5 7.9975 1.5C10.4124 1.5 12.37 3.45763 12.37 5.8725C12.37 7.49094 11.4907 8.90401 10.1837 9.66004Z" fill="#204A65"/>
        </svg>
      `;

      return `
        <div class="rental-banner access-program-style">
          <span class="tooltip-trigger access-program-tooltip" tabindex="0" role="button" aria-haspopup="dialog" aria-label="${ProductDisplayManager.escapeAccessProgramHtml(String(bannerText).replace(/<[^>]+>/g, ''))}">
            ${icon}
            <span class="access-program-text">${bannerText}</span>
            <span class="access-program-tooltip-content" role="tooltip" aria-hidden="true">
              <span class="access-program-tooltip-header">
                ${icon}
                <span class="access-program-tooltip-title">
                  ${tooltipTitle}
                </span>
              </span>
              <span class="access-program-tooltip-body">
                ${tooltipMessage}
              </span>
            </span>
          </span>
        </div>
      `;
    };

    // Find section access data
    const filteredSectionAccess = ProductDisplayManager._sectionsById?.get(
      String(sectionData.courseInfo.selectedsectionId ?? '')
    );

    const terms = Array.isArray(window.courseData?.ddcs_term)
      ? window.courseData.ddcs_term
      : [];
    const campuses = Array.isArray(window.courseData?.ddcs_campus_entity)
      ? window.courseData.ddcs_campus_entity
      : [];
    const filteredTermSelected = terms.find(
      term => term.term_id === sectionData.courseInfo.termId
    );
    const campusEntityId =
      filteredTermSelected?.campus_entity_id ||
      filteredSectionAccess?.campus_entity_id ||
      '';
    const filteredAccessProgramName = campuses.find(
      campus => campus.campus_entity_id === campusEntityId
    );
    
    const hasAccessProgram = filteredSectionAccess?.included_enabled_flag;
    const includedProgramName = formatAccessProgramName(
      filteredAccessProgramName?.included_program_name,
    );
    const accessProgramNote =
      includedProgramName ||
      formatAccessProgramName(filteredSectionAccess?.included_section_note);
    
    // Build course title HTML
    const courseTitleHTML = `<div class="course-title">${sectionData.courseInfo.department} ${sectionData.courseInfo.course}</div>`;
    
    // Build course details HTML
    const courseDetailsHTML = hasAccessProgram
      ? `
        <div class="course-access">
          ${courseTitleHTML}
          ${getAccessProgramBannerHTML(accessProgramNote)}
        </div>
      `
      : courseTitleHTML;

    const requiredLabel =
      requiredCount > 0 ? `${requiredCount} Required` : '';

    // Generate header HTML (structure matches CMRP Figma: mobile stacks title → count → section; desktop uses grid)
    headerSection.innerHTML = `
      <div class="header-content">
        <div class="course-details">
          ${courseDetailsHTML}
          ${
            requiredLabel
              ? `<div class="required-count">${requiredLabel}</div>`
              : ''
          }
          <div class="section-info">Section: ${sectionNumber}${instructorName === 'TBA' ? '' : ` | Instructor: ${instructorName}`}</div>
        </div>
        <div class="header-actions">
          ${getRemoveButtonHTML()}
        </div>
      </div>
    `;
    courseCard.appendChild(headerSection);
    if (sectionCourseNotes) {
      courseCard.appendChild(sectionCourseNotes);
    }

    const materialsSection = await this.createMaterialsSection(sectionData, choice_minimum, department);
    courseCard.appendChild(materialsSection);
    section.appendChild(courseCard);
    productsContainer.appendChild(section);

    // Trigger event that materials are loaded
    document.dispatchEvent(new CustomEvent('materialsLoaded'));

    // Update In Bag indicators after materials are loaded
    this.updateInBagIndicatorsForSection();
    this.updatePurchaseOptions();
    ProductDisplayManager._perfEnd(perfDept, {
      products: sectionData.products?.length ?? 0,
    });
  }
  updatePurchaseOptions(){
    const currentUrl = new URL(window.location.href);
    const shopByValue = currentUrl.searchParams.get('shopBy');
    const checkboxLabels = document.querySelectorAll('.checkbox-label');
    const removebtn = document.querySelectorAll('.remove-btn');
    if (shopByValue === 'discoverview') {    
      checkboxLabels.forEach(label => {    
        // @ts-ignore
        label.style.display = 'none';
      });
      removebtn.forEach(label => {    
        // @ts-ignore
        label.style.display = 'none';
      });
    }else{
      checkboxLabels.forEach(label => {    
        // @ts-ignore
        label.style.display = 'inline-block';
      });
      removebtn.forEach(label => {    
        // @ts-ignore
        label.style.display = 'flex';
      });
    }
  }
  /**
   * Create materials section for a course
   * @param {any} sectionData - Section data
   * @returns {HTMLElement} Materials section element
   * @private
   */
  async createMaterialsSection(sectionData, choice_minimum, department = '') {
    const materialsSection = document.createElement('div');
    materialsSection.className = 'materials-section';

    if (sectionData.products.length === 0) {
        const noteText = sectionData._cmrpSamOnlyAllSam
          ? ProductDisplayManager.getCmrpSamHiddenMessage()
          : "Materials aren't required for this course.";
        const adoptionStyle = document.createElement('div');
        adoptionStyle.className = 'no-materials-message';
        adoptionStyle.innerHTML = `
            <div class="adoption-message-content">
                <div class="adoption-icon">${this.ADOPTION_STATUS_ICON}</div>
                <div class="adoption-text">
                    <h4 class="adoption-title">Course Materials Status</h4>
                    <p class="adoption-note"></p>
                </div>
            </div>
        `;
        const noteEl = adoptionStyle.querySelector('.adoption-note');
        if (noteEl) {
          noteEl.textContent = noteText;
        }
        materialsSection.appendChild(adoptionStyle);
        return materialsSection;
    }

    const courseInfo = sectionData.courseInfo;
    const matchesSectionRow = p =>
      ProductDisplayManager._productRowMatchesCourseSectionIds(p, courseInfo);

    // Separate suggested materials from other materials
    const suggestedProducts = ProductDisplayManager._sortProductsByMaterialThenTitle(
      sectionData.products.filter(
        /** @param {any} p */ p =>
            p.material_type === 'Suggested' && matchesSectionRow(p)
      )
    );

    const choiceProducts = ProductDisplayManager._sortProductsByMaterialThenTitle(
      ProductDisplayManager._mergeUnifiedCmrpProducts(
        sectionData.products.filter(
          /** @param {any} p */ p => (
              p.material_type === 'Choice' ||
              p.material_type === 'Choice/Recommended' ||
              p.material_type === 'Choice/Required')
              &&
              matchesSectionRow(p)
        )
      )
    );

    const visibleProducts = sectionData.products.filter(
      /** @param {any} p */ p =>
          (p.material_type === 'Required' ||
          p.material_type === 'Recommended' ||            
          !p.material_type ||
          p.material_type.trim() === '') &&
          p.material_type !== 'Suggested' &&
          p.material_type !== 'Choice' &&
          p.material_type !== 'Choice/Recommended' &&
          p.material_type !== 'Choice/Required' // Exclude suggested/choice from main list
    );
    const filteredItems = ProductDisplayManager._sortProductsByMaterialThenTitle(
      visibleProducts.filter(matchesSectionRow)
    );

    // Merge unified print/digital inside buildBundleDisplayList so package
    // children keep their own title_id matches instead of being absorbed by a
    // standalone ISBN counterpart that is not in package_items.
    const filteredProductsSC = filteredItems
      .map(product => {
        const productSystem = window.ProductSystem || {};
        const hasSc =
          productSystem.salesChannelTokensContainSc ||
          this.productHandler.salesChannelTokensContainSc.bind(this.productHandler);
        const sectionIdNumber = sectionData?.courseInfo?.selectedsectionId;

        const variantIsCEBCourseMaterials = variant => {
          const mf = variant?.metafields;
          if (Array.isArray(mf)) {
            return mf.some(
              x =>
                x &&
                x.namespace === 'course_materials' &&
                (x.book_type_ind === 'CEB' ||
                  (x.key === 'book_type_ind' && String(x.value) === 'CEB'))
            );
          }
          if (mf && typeof mf === 'object') {
            return (
              (mf.book_type_ind === 'CEB' && mf.namespace === 'course_materials') ||
              mf.course_materials?.book_type_ind === 'CEB'
            );
          }
          return false;
        };

        // Sales Channel rule applied to digital CEB products and "no format" products (options: ["Type"]).
        const options = Array.isArray(product?.options) ? product.options : [];
        const isNoFormatTypeProduct =
          options.length === 1 &&
          String(
            typeof options[0] === 'object' && options[0] !== null
              ? (options[0].name ?? options[0].value ?? '')
              : options[0]
          ).trim() === "Type";

        if (product?.metafields?.book_type_ind !== "CEB" && !isNoFormatTypeProduct) {
          if(product.metafields.book_type_ind === "TXT"){
            const variants = product.variants || [];            
            const hasCEBVariant = variants.some(variantIsCEBCourseMaterials);
            if(hasCEBVariant){
              let mapped_section = null;
              if (product.metafields?.course_materials?.section_data_mapping) {
                try {
                  const section_data_mapping = JSON.parse(product.metafields.course_materials.section_data_mapping);
                  if (Array.isArray(section_data_mapping) && section_data_mapping.length > 0) {
                    mapped_section = section_data_mapping.find(
                      item => String(item?.SectionID) === String(sectionIdNumber)
                    );
                  }
                } catch (error) {
                  console.error("Error parsing section_data_mapping:", error);
                }
              }
              const accessProgramSection =
                mapped_section &&
                (mapped_section.IncludEDMaterialFlag === "true" || mapped_section.IncludEDMaterialFlag === true);

              if (!accessProgramSection) {
                return {
                  ...product,
                  hideSalesChannel: false
                };
              }

              const keptVariants = Array.isArray(product.variants)
                ? product.variants.filter(variant => {
                    if (!variantIsCEBCourseMaterials(variant)) {
                      return true;
                    }
                    const mf = variant?.metafields;
                    let cmDigitalRaw = '';
                    let customRaw = '';

                    if (Array.isArray(mf)) {
                      const cmDigitalRow = mf.find(
                        x =>
                          x &&
                          x.namespace === 'cm_digital' &&
                          (x.key === 'sales_channel' || x.sales_channel != null)
                      );
                      if (cmDigitalRow?.sales_channel != null) {
                        cmDigitalRaw = String(cmDigitalRow.sales_channel);
                      }

                      const customRow = mf.find(
                        x =>
                          x &&
                          x.namespace === 'custom' &&
                          (x.key === 'sales_channel' || x.sales_channel != null)
                      );
                      if (customRow?.sales_channel != null) {
                        customRaw = String(customRow.sales_channel);
                      }
                    } else if (mf && typeof mf === 'object') {
                      if (mf.cm_digital?.sales_channel != null) {
                        cmDigitalRaw = String(mf.cm_digital.sales_channel);
                      }
                      if (mf.custom?.sales_channel != null) {
                        customRaw = String(mf.custom.sales_channel);
                      }
                    }

                    if (cmDigitalRaw) {
                      return hasSc(cmDigitalRaw);
                    }
                    if (customRaw) {
                      return hasSc(customRaw);
                    }
                    return false;
                  })
                : [];
              const allDigitalVariantsWithoutSc = keptVariants.length === 0;
                
              return {
                ...product,
                variants: keptVariants,
                hideSalesChannel: allDigitalVariantsWithoutSc
              };
            }else{
              return {
                ...product,
                hideSalesChannel: false
              };
            }
          }else{
            return {
              ...product,
              hideSalesChannel: false
            };
          }
          
        }

        let mapped_section = null;
        if (product.metafields?.course_materials?.section_data_mapping) {
          try {
            const section_data_mapping = JSON.parse(product.metafields.course_materials.section_data_mapping);
            if (Array.isArray(section_data_mapping) && section_data_mapping.length > 0) {
              mapped_section = section_data_mapping.find(
                item => String(item?.SectionID) === String(sectionIdNumber)
              );
            }
          } catch (error) {
            console.error("Error parsing section_data_mapping:", error);
          }
        }

        const accessProgramSection =
          mapped_section &&
          (mapped_section.IncludEDMaterialFlag === "true" || mapped_section.IncludEDMaterialFlag === true);

        if (!accessProgramSection) {
          return {
            ...product,
            hideSalesChannel: false
          };
        }

        const keptVariants = Array.isArray(product.variants)
          ? product.variants.filter(variant => {
              if (!variantIsCEBCourseMaterials(variant)) {
                return true;
              }
              const mf = variant?.metafields;
              let cmDigitalRaw = '';
              let customRaw = '';

              if (Array.isArray(mf)) {
                const cmDigitalRow = mf.find(
                  x =>
                    x &&
                    x.namespace === 'cm_digital' &&
                    (x.key === 'sales_channel' || x.sales_channel != null)
                );
                if (cmDigitalRow?.sales_channel != null) {
                  cmDigitalRaw = String(cmDigitalRow.sales_channel);
                }

                const customRow = mf.find(
                  x =>
                    x &&
                    x.namespace === 'custom' &&
                    (x.key === 'sales_channel' || x.sales_channel != null)
                );
                if (customRow?.sales_channel != null) {
                  customRaw = String(customRow.sales_channel);
                }
              } else if (mf && typeof mf === 'object') {
                if (mf.cm_digital?.sales_channel != null) {
                  cmDigitalRaw = String(mf.cm_digital.sales_channel);
                }
                if (mf.custom?.sales_channel != null) {
                  customRaw = String(mf.custom.sales_channel);
                }
              }

              if (cmDigitalRaw) {
                return hasSc(cmDigitalRaw);
              }
              if (customRaw) {
                return hasSc(customRaw);
              }
              return false;
            })
          : [];
        const allDigitalVariantsWithoutSc = keptVariants.length === 0;
          
        return {
          ...product,
          variants: keptVariants,
          hideSalesChannel: allDigitalVariantsWithoutSc
        };
      });
    const sanitizedDept = String(department).replace(/[^a-zA-Z0-9-]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '') || 's';
    const bundleDisplayList = this.buildBundleDisplayList(filteredProductsSC);
    const displaySlots = bundleDisplayList.map(row => ({
      kind: 'product',
      materialType: row.parentProduct?.material_type || '',
      row,
    }));
    if (choiceProducts.length > 0) {
      displaySlots.push({
        kind: 'choice',
        materialType: ProductDisplayManager._choiceSlotMaterialType(choiceProducts),
      });
    }
    displaySlots.sort(ProductDisplayManager._compareDisplaySlots);

    let productSlotIndex = 0;
    for (let i = 0; i < displaySlots.length; i++) {
      const slot = displaySlots[i];
      if (slot.kind === 'choice') {
        const choiceSection = await this.createChoiceProductsSection(
          choiceProducts,
          sectionData,
          choice_minimum,
          sanitizedDept
        );
        materialsSection.appendChild(choiceSection);
        continue;
      }

      const { parentProduct, childProducts } = slot.row;
      const instanceId = `${sanitizedDept}-${productSlotIndex}`;
      productSlotIndex += 1;
      const materialItem = await this.createMaterialItem(parentProduct, sectionData, instanceId, { childProducts });
      materialsSection.appendChild(materialItem);

      /** @type {any} */
      const globalWindow = window;
      if (typeof globalWindow.addMaterialEventListeners === 'function') {
        globalWindow.addMaterialEventListeners(materialItem, parentProduct);
      }
    }
    
    
    // Render suggested materials component at the bottom, after all other materials
    if (suggestedProducts.length > 0) {
      await this.renderSuggestedMaterialsComponent(materialsSection, suggestedProducts, sectionData);
    }

    return materialsSection;
  }

  /**
   * Build the Choice materials slot (one section, options sorted Required → Recommended, then by title).
   * @param {Array<any>} choiceProducts
   * @param {any} sectionData
   * @param {string} choice_minimum
   * @param {string} sanitizedDept
   * @returns {Promise<HTMLElement>}
   * @private
   */
  async createChoiceProductsSection(choiceProducts, sectionData, choice_minimum, sanitizedDept) {
    const isInlineChoice =
      typeof window !== 'undefined' &&
      ((window.matchMedia &&
        window.matchMedia('(max-width: 968px)').matches) ||
        window.innerWidth <= 968);
    const isHeoa =
      typeof window !== 'undefined' &&
      (window.__follettDiscoverViewFlow === true ||
        window.ProductSystem?.isFollettDiscoverViewPage?.() === true);

    const materialItemChoice = document.createElement('div');
    materialItemChoice.className = 'choice-list choice-products-section';
    if (!isInlineChoice) {
      materialItemChoice.classList.add('material-item');
    }

    const choiceInstruction = document.createElement('div');
    choiceInstruction.className = 'choice-instruction';
    const progressHtml = isHeoa
      ? ''
      : `<div class="choice-products-header__progress is-empty" id="choice-progress-${sectionData.courseInfo.selectedsectionId}" data-choice-min="${choice_minimum}">
              <svg class="choice-products-header__progress-check" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <circle cx="8" cy="8" r="8" fill="currentColor"/>
                <path d="M4.75 8.2L6.9 10.35L11.4 5.7" stroke="#fff" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              <span class="choice-products-header__progress-text">0/${choice_minimum} Selected</span>
            </div>`;
    const choiceHintLead = isHeoa
      ? `You'll choose ${choice_minimum}`
      : `Choose ${choice_minimum}`;
    choiceInstruction.innerHTML = `
          <input type="hidden" id="choice-minimum-${sectionData.courseInfo.selectedsectionId}" value="${choice_minimum}" />
          <div class="choice-products-header">
            <span class="choice-products-header__badge">Required</span>
            <p class="choice-products-header__hint"><span class="choice-products-header__hint-lead">${choiceHintLead}</span> out of ${choiceProducts.length} materials</p>
            ${progressHtml}
          </div>
      `;
    materialItemChoice.appendChild(choiceInstruction);

    const choiceItems = document.createElement('div');
    choiceItems.id = 'choice-items-'+sectionData.courseInfo.selectedsectionId;
    choiceItems.className = 'choice-products-grid';

    for (let i = 0; i < choiceProducts.length; i++) {
      const product = choiceProducts[i];
      const instanceId = `${sanitizedDept}-choice-${i}`;
      const materialItem = await this.createMaterialItemChoice(product, sectionData, instanceId);
      choiceItems.appendChild(materialItem);

      /** @type {any} */
      const globalWindow = window;
      if (typeof globalWindow.addMaterialEventListeners === 'function') {
        globalWindow.addMaterialEventListeners(materialItem, product);
      }
    }

    materialItemChoice.appendChild(choiceItems);
    const choiceFinalItems = document.createElement('div');
    choiceFinalItems.id = 'choice-final-items-'+sectionData.courseInfo.selectedsectionId;
    if (isHeoa) {
      choiceItems.classList.add('hide-choice-items');
      choiceFinalItems.classList.add('show-choice-items');
    } else {
      choiceFinalItems.classList.add('hide-choice-items');
    }
    await this.renderChoiceMaterialsComponent(choiceFinalItems, choiceProducts, sectionData);
    materialItemChoice.appendChild(choiceFinalItems);
    return materialItemChoice;
  }

  /**
   * Render Choice materials using Preact component
   * @param {HTMLElement} materialsSection - Parent materials section container
   * @param {Array<any>} choiceProducts - Array of choice products
   * @param {Object} sectionData - Section data
   * @private
   */
  async renderChoiceMaterialsComponent(materialItemChoice, choiceProducts, sectionData) {
    // Ensure window.createMaterialItemHTML is available for the Preact component
    // Store reference to 'this' for use in the function
    const self = this;
    if (typeof window !== 'undefined' && !window.createMaterialItemHTML) {
      /** @type {any} */
      const globalWindow = window;
      globalWindow.createMaterialItemHTML = async function(product, sectionData) {
        // Create a wrapper div and get the HTML
        const wrapper = document.createElement('div');
        const html = await self.renderMaterialItemTemplate(product, sectionData);
        wrapper.innerHTML = html;
        return wrapper.innerHTML;
      };
    }

    // Create the custom element
    // The component module should be loaded by the entry point (preact-theme.js)
    // preact-custom-element will automatically handle rendering when the element is added to DOM
    const choiceComponent = document.createElement('choice-products-component');
    
    // Set attributes with JSON stringified data
    const choiceProductsJson = JSON.stringify(choiceProducts);
    choiceComponent.setAttribute('products-data', choiceProductsJson);
    choiceComponent.setAttribute('section-data', JSON.stringify(sectionData));
    choiceComponent.setAttribute('price-data', JSON.stringify([]));
    choiceComponent.dataset.cmrpProducts = choiceProductsJson;
    
    // Append to materials section at the end (after all other material items)
    // Using appendChild ensures it's added as the last child
    materialItemChoice.appendChild(choiceComponent);
  }
  /**
   * Render suggested materials using Preact component
   * @param {HTMLElement} materialsSection - Parent materials section container
   * @param {Array<any>} suggestedProducts - Array of suggested products
   * @param {Object} sectionData - Section data
   * @private
   */
  async renderSuggestedMaterialsComponent(materialsSection, suggestedProducts, sectionData) {
    // Ensure window.createMaterialItemHTML is available for the Preact component
    // Store reference to 'this' for use in the function
    const self = this;
    if (typeof window !== 'undefined' && !window.createMaterialItemHTML) {
      /** @type {any} */
      const globalWindow = window;
      globalWindow.createMaterialItemHTML = async function(product, sectionData) {
        // Create a wrapper div and get the HTML
        const wrapper = document.createElement('div');
        const html = await self.renderMaterialItemTemplate(product, sectionData);
        wrapper.innerHTML = html;
        return wrapper.innerHTML;
      };
    }

    // Create the custom element
    // The component module should be loaded by the entry point (preact-theme.js)
    // preact-custom-element will automatically handle rendering when the element is added to DOM
    const suggestedComponent = document.createElement('suggested-materials-component');
    
    // Set attributes with JSON stringified data
    const suggestedProductsJson = JSON.stringify(suggestedProducts);
    suggestedComponent.setAttribute('products-data', suggestedProductsJson);
    suggestedComponent.setAttribute('section-data', JSON.stringify(sectionData));
    suggestedComponent.dataset.cmrpProducts = suggestedProductsJson;
    
    // Append to materials section at the end (after all other material items)
    // Using appendChild ensures it's added as the last child
    materialsSection.appendChild(suggestedComponent);
  }
  getPriceRangeForProducts(choiceProduct) {
    if (!choiceProduct.variants) return { title: choiceProduct.title, priceRange: 'N/A', startingPrice: 'N/A' };
        
    const availableVariants = choiceProduct.variants.filter(variant => variant.available);

    
    if (availableVariants.length === 0) return { title: choiceProduct.title, priceRange: 'N/A', startingPrice: 'N/A' };

    const prices = availableVariants
        .map(variant => parseFloat(variant.price))
        .filter(price => !isNaN(price));

    
    const minPrice = Math.min(...prices);
    const maxPrice = Math.max(...prices);
    const startingPrice =
      availableVariants.length > 1 || minPrice !== maxPrice
        ? `$${minPrice.toFixed(2)}+`
        : `$${minPrice.toFixed(2)}`;

    return {
      title: choiceProduct.title,
      priceRange: `$${minPrice.toFixed(2)} - $${maxPrice.toFixed(2)}`,
      startingPrice
    };
  }
  /**
   * Render material item template (mimics Liquid snippet)
   * @param {any} product - Product data
   * @returns {string} HTML string
   * @private
   */
  // @ts-ignore
  async renderMaterialItemTemplateChoice(product, sectionData, instanceId = '') {
    // Extract product data
   
    const authorName = this.productHandler.extractAuthorName(product) || '';
    const isbn =
      this.productHandler.extractISBNFromProduct(product) ||
      ProductDisplayManager._productIsbn(product) ||
      'Not specified';
    // @ts-ignore
    const formatButtons = this.productHandler.generateFormatButtons(product);
    
    
    const prod_formats = this.productHandler.getVariantUniques(product);
    // Badge HTML (conditional)
    

    let htmlOutput;

    const showDigitalDeliveryNotice =
      await ProductDisplayManager.getDigitalDeliveryNoticeVisible();
    const digitalDeliveryNoticeHtml = showDigitalDeliveryNotice
      ? '<div class="digital-delivery-notice">Digital delivery fees may apply.</div>'
      : '';
    
    if (prod_formats.length === 1) {
        if (prod_formats[0] === "Digital") {
            const initialPurchaseOptions = await this.productHandler.generateInitialPurchaseOptions("Digital", product, sectionData, instanceId);
            htmlOutput = `
        ${buildFormatPurchasePanelHtml({
          format: 'digital',
          label: 'Digital',
          productId: product.id,
          instanceId,
          collapseContentHtml: `
                        ${digitalDeliveryNoticeHtml}
                        <div class="purchase-types purchase-types-CMRP">
                            ${initialPurchaseOptions}
                        </div>`,
        })}
        <div class="material-notify-container" style="display: none;">
            <!-- Notify me button will be added here when needed -->
        </div>`;
        } else if (prod_formats[0] === "Print") {
            const print = await this.productHandler.generateInitialPurchaseOptions("Print", product, sectionData, instanceId);
            htmlOutput = `
        ${buildFormatPurchasePanelHtml({
          format: 'print',
          label: 'Physical',
          productId: product.id,
          instanceId,
          collapseContentHtml: `
                        <div class="purchase-types purchase-types-CMRP">
                            ${print}
                        </div>`,
        })}
        <div class="material-notify-container" style="display: none;">
            <!-- Notify me button will be added here when needed -->
        </div>`;
        } else {
          const initialPurchaseOptions = await this.productHandler.generateInitialPurchaseOptions("", product, sectionData, instanceId);
          const simpleGroupId = buildPurchaseGroupLabelId({
            format: 'options',
            productId: product.id,
            instanceId,
          });
          htmlOutput = `
            <!-- Simple product - direct purchase options -->
            <div class="simple-product-purchase" role="group" aria-labelledby="${simpleGroupId}">
              <h4 id="${simpleGroupId}" class="cmrp-purchase-group-label">Purchase options</h4>
              <div class="format-content-area">
                  <div class="format-collapse active" style="display: block;">
                      <div class="collapse-content">
                          <div class="purchase-types purchase-types-CMRP">
                            ${initialPurchaseOptions}
                          </div>
                      </div>
                  </div>
              </div>
            </div>
            <div class="material-notify-container" style="display: none;">
              <!-- Notify me button will be added here when needed -->
            </div>
              `;
        }
    } else if (prod_formats.length === 2 && prod_formats.includes("Digital") && prod_formats.includes("Print")) {
        const digital = await this.productHandler.generateInitialPurchaseOptions("Digital", product, sectionData, instanceId);
        const print = await this.productHandler.generateInitialPurchaseOptions("Print", product, sectionData, instanceId);
        const printPanel = buildFormatPurchasePanelHtml({
          format: 'print',
          label: 'Physical',
          productId: product.id,
          instanceId,
          includeWarning: true,
          collapseContentHtml: `
                        <div class="purchase-types purchase-types-CMRP">
                            ${print}
                        </div>`,
        });
        const digitalPanel = buildFormatPurchasePanelHtml({
          format: 'digital',
          label: 'Digital',
          productId: product.id,
          instanceId,
          collapseContentHtml: `
                        ${digitalDeliveryNoticeHtml}
                        <div class="purchase-types purchase-types-CMRP">
                            ${digital}
                        </div>`,
          footerHtml: `
            <div class="delivery-notice" data-format-specific="digital" style="display: none;">
                <span id="digital-fee-notice">*Digital delivery fee of $<span class="digital-fee-amount">5.99</span> may apply</span> | 
                <button type="button" class="requirements-link" onclick="openRequirementsDialog()">
                    Requirements
                </button>
            </div>`,
        });
        htmlOutput = `
        ${printPanel}
        ${digitalPanel}
        <div class="material-notify-container" style="display: none;">
            <!-- Notify me button will be added here when needed -->
        </div>`;
    } 

    const details = this._extractProductDetails(product);
    const detailsData = this._generateDetailsHTML(details);
    
    const isAdoptedTitle = product.tags && product.tags.includes("adoptedsupply");
    const priceRange = this.getPriceRangeForProducts(product);
    
    const authorBlock =
      authorName && String(authorName).trim()
        ? `<div class="material-author choice-product-card__author">${authorName}</div>`
        : `<div class="material-author choice-product-card__author choice-product-card__author--empty" aria-hidden="true">&nbsp;</div>`;
    const displayIsbn = isbn !== 'Not specified' ? isbn : '';
    const isbnBlock =
      isAdoptedTitle === false
        ? `<div class="choice-meta-row"${displayIsbn ? '' : ' hidden'}><span class="choice-meta-label">ISBN:</span><span class="choice-meta-value" data-choice-original-isbn="${displayIsbn}">${displayIsbn}</span></div>`
        : '';
    const startingPrice = priceRange.startingPrice || priceRange.priceRange || '';

    return `
      <div class="material-item choice-material-item choice-product-card" role="article" aria-labelledby="${buildMaterialTitleId({ productId: product.id, instanceId })}">
        <div class="choice-product-card__inner">
        <div class="material-information" onclick="handleMaterialItemClick(event)">
            <div class="material-content choice-product-card__content">
                <div class="choice-product-card__image-wrap">
                    <img src="${product.image}" alt="" class="material-image" />
                </div>
                <div class="material-text-content choice-product-card__text">
                    <div class="material-text">
                        <div class="choice-product-card__title-row">
                        <h3 class="material-title choice-product-card__title" tabindex="0" id="${buildMaterialTitleId({ productId: product.id, instanceId })}">${product.title}</h3>
                        <div class="choice-product-card__aside">
                    <button type="button" class="choice-product-card__plus-select choice-plus-button-${sectionData.courseInfo.selectedsectionId}" id="plus-box-product-${product.id}-${instanceId}" data-product-id="${product.id}" aria-expanded="false" aria-label="Open purchase options for ${String(product.title || '').replace(/"/g, '&quot;')}">
                        <span class="choice-plus__icon" aria-hidden="true">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true" focusable="false">
                            <path d="M8 3.5V12.5M3.5 8H12.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                        </span>
                        <span class="choice-plus__count" aria-hidden="true" hidden></span>
                        <span class="choice-plus__edit" aria-hidden="true">
                        <svg width="16" height="16" viewBox="0 0 13.3333 13.3333" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
                            <path fill-rule="evenodd" clip-rule="evenodd" d="M10.9549 1.33333C10.8177 1.33333 10.6818 1.36036 10.555 1.41288C10.4282 1.4654 10.313 1.54238 10.216 1.63943L2.17106 9.68433L1.61683 11.7165L3.64902 11.1623L11.6939 3.11739C11.791 3.02034 11.8679 2.90513 11.9205 2.77834C11.973 2.65155 12 2.51565 12 2.37841C12 2.24116 11.973 2.10527 11.9205 1.97847C11.8679 1.85168 11.791 1.73647 11.6939 1.63943C11.5969 1.54238 11.4817 1.4654 11.3549 1.41288C11.2281 1.36036 11.0922 1.33333 10.9549 1.33333ZM10.0448 0.181045C10.3333 0.0615192 10.6426 0 10.9549 0C11.2673 0 11.5766 0.0615192 11.8651 0.181045C12.1537 0.300571 12.4159 0.475764 12.6367 0.696619C12.8576 0.917474 13.0328 1.17967 13.1523 1.46823C13.2718 1.75679 13.3333 2.06607 13.3333 2.37841C13.3333 2.69074 13.2718 3.00002 13.1523 3.28858C13.0328 3.57715 12.8576 3.83934 12.6367 4.0602L4.46664 12.2303C4.3846 12.3123 4.28258 12.3715 4.17065 12.4021L0.842094 13.3098C0.611286 13.3728 0.364444 13.3072 0.195278 13.1381C0.0261108 12.9689 -0.0394414 12.7221 0.0235062 12.4913L0.931294 9.1627C0.96182 9.05077 1.02103 8.94874 1.10307 8.86671L9.27315 0.696619C9.49401 0.475764 9.7562 0.300571 10.0448 0.181045Z" fill="currentColor"/>
                        </svg>
                        </span>
                    </button>
        <div class="choice-price-material">
          <div class="check-price-material">
            <div class="material-title " id="price-range-choice-${product.id}">${priceRange.priceRange}</div>
            <div class="material-title hide-price-choice" id="price-choice-${product.id}"></div>
            <div class="purchase-choice-checkbox-container" onclick="event.stopPropagation()">
              <input type="checkbox" id="check-box-product-${product.id}" class="purchase-choice-checkbox view-check-btn-choice" data-section-id="${sectionData.courseInfo.selectedsectionId}">
              <label for="check-box-product-${product.id}" class="checkbox-label" style="display: inline-block;"></label>
            </div>
          </div>
          <div class="material-view-more material-view-more-choice" id="div-view-details-${product.id}">
            <button class="view-more-btn-choice" type="button" id="choice-info-product-${product.id}" aria-expanded="false">
              <span>View details</span>
                  <svg class="chevron-down" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true" focusable="false">
                      <path d="M4 6L8 10L12 6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path>
                  </svg>
              </button>
          </div>
        </div>
        </div>
                        </div>
                        <div class="material-details choice-product-card__details">
                            ${authorBlock}
                            ${isbnBlock}
                            ${startingPrice && startingPrice !== 'N/A' ? `<div class="choice-product-card__price" data-choice-original-price="${startingPrice}">${startingPrice}</div>` : ''}
                        </div>
                    </div>
                </div>
            </div>
            <button type="button" class="choice-product-card__view-options" data-product-id="${product.id}" aria-expanded="false">
                <span class="choice-product-card__view-options-label">View Options</span>${startingPrice && startingPrice !== 'N/A' ? `<span class="choice-product-card__view-options-price" data-choice-original-price="${startingPrice}">${startingPrice}</span>` : ''}
            </button>
            <div class="choice-product-card__selected-summary" hidden>
              <div class="choice-product-card__selected-meta">
                <div class="choice-product-card__selected-format"></div>
                <div class="choice-product-card__selected-price"></div>
                <div class="choice-product-card__selected-due" hidden></div>
              </div>
              <div class="choice-product-card__selected-actions">
                <button type="button" class="choice-product-card__edit" data-product-id="${product.id}">Edit</button>
                <button type="button" class="choice-product-card__remove" data-product-id="${product.id}">Remove</button>
              </div>
            </div>
        </div>
        </div>
      </div>
      <div class="material-item choice-info choice-hide" id="choice-details-product-${product.id}" role="presentation">
        <div class="choice-drawer-backdrop" aria-hidden="true"></div>
        <div class="choice-drawer-sheet" role="dialog" aria-modal="true" aria-labelledby="choice-drawer-title-${product.id}">
          <div class="choice-drawer-header">
            <span class="choice-drawer-title" id="choice-drawer-title-${product.id}">Choose purchase options</span>
            <button type="button" class="choice-drawer-close" aria-label="Close">
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path d="M5 5l8 8M13 5l-8 8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
              </svg>
            </button>
          </div>
          <div class="choice-drawer-scroll-clip">
          <div class="choice-drawer-scroll">
            <div class="material-information" onclick="handleMaterialItemClick(event)">
              <div class="material-content">
                <div class="material-text-content">
                  <div class="material-text">
                    <div class="material-details">
                      <div class="aditional-info-title">Additional Textbook Details</div>
                      ${detailsData !== '' ? detailsData : ''}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="material-purchase-options">
              ${htmlOutput}
            </div>
          </div>
          </div>
        </div>
      </div>
    `;
  }
   /**
   * Create a material item element
   * @param {any} product - Product data
   * @returns {HTMLElement} Material item element
   * @private
   */
  // @ts-ignore
  async createMaterialItemChoice(product, sectionData, instanceId = '') {
    const materialItem = document.createElement('div');
    materialItem.className = 'material-item';
    materialItem.classList.add('choice-item');
    if (instanceId) materialItem.dataset.purchaseInstanceId = instanceId;

    materialItem.innerHTML = await this.renderMaterialItemTemplateChoice(
      product,
      sectionData,
      instanceId
    );

    return materialItem;
  }
  /**
   * Create a material item element
   * @param {any} product - Product data
   * @returns {HTMLElement} Material item element
   * @private
   */
  // @ts-ignore
  async createMaterialItem(product, sectionData, instanceId = '', options = {}) {
    const childProducts = options.childProducts || [];
    const isBundle = childProducts.length > 0;

    const materialItem = document.createElement('div');
    materialItem.className = 'material-item' + (isBundle ? ' material-item--bundle' : '');
    if (instanceId) {
      materialItem.dataset.purchaseInstanceId = instanceId;
    } else {
      const sectionId = sectionData?.courseInfo?.selectedsectionId || '';
      if (sectionId) materialItem.dataset.purchaseInstanceId = sectionId;
    }
    if (isBundle && childProducts.length) {
      materialItem.dataset.bundleChildIds = childProducts.map((p) => String(p.id)).join(',');
      materialItem.dataset.cmrpBundleParent = JSON.stringify(product);
      materialItem.dataset.cmrpBundleProducts = JSON.stringify(childProducts);
    }

    if (!isBundle) {
      // Preserve legacy DOM structure for standard CMRP items so existing layout/CSS remains unchanged.
      materialItem.innerHTML = await this.renderMaterialItemTemplate(product, sectionData, instanceId);
      return materialItem;
    }

    const mainWrapper = document.createElement('div');
    mainWrapper.className = 'material-item-main';
    mainWrapper.innerHTML = await this.renderMaterialItemTemplate(product, sectionData, instanceId);
    materialItem.appendChild(mainWrapper);

    if (isBundle) {
      const purchaseOptionsForBundle =
        mainWrapper.querySelector('.material-purchase-options') || materialItem;
      const bundleComponent = document.createElement('bundle-products-component');
      const bundleChildJson = JSON.stringify(childProducts);
      bundleComponent.setAttribute('products-data', bundleChildJson);
      bundleComponent.setAttribute('section-data', JSON.stringify(sectionData));
      bundleComponent.setAttribute('view-mode', 'inline');
      bundleComponent.dataset.cmrpProducts = bundleChildJson;
      purchaseOptionsForBundle.appendChild(bundleComponent);
    }

    return materialItem;
  }
  _extractProductDetails(product) {
    return {
      'Publisher':
        this.productHandler?.extractPublisher?.(product) || 'Not specified',
      'Format':
        this.productHandler?.extractFormat?.(product) || 'Not specified',
      'Copyright':
        this.productHandler?.extractCopyRight?.(product) || 'Not specified',
      'Publication':
        this.productHandler?.extractPublication?.(product) || 'Not specified'
    };
  }
  _generateDetailsHTML(details) {
    const rows = Object.entries(details)
      .map(
        ([key, value]) =>
          value === "Not specified" 
            ? ''  // Retorna uma string vazia se o valor for "Not Specficated"
            : `<div class="detail-row">${key}: <span class="detail-value">${value}</span></div>`
      )
      .join('');
   
    
    return rows;
  }

  /**
   * PDP shortcut on card image (bottom-right). SVG matches assets/cmrp-pdp-link-icon.svg.
   * @param {string} [productUrl]
   * @returns {string}
   */
  materialPdpLinkHTML(productUrl, productTitle = '') {
    const href = String(productUrl || '')
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;');
    const title = String(productTitle || '').replace(/"/g, '&quot;');
    const detailsLabel = title
      ? `View product details for ${title}`
      : 'View product details';
    return `<a href="${href}" class="material-image-pdp-link" aria-label="${detailsLabel}" onclick="event.stopPropagation()" rel="noopener noreferrer"><svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><rect x="0.5" y="0.5" width="39" height="39" rx="19.5" fill="white"/><rect x="0.5" y="0.5" width="39" height="39" rx="19.5" stroke="#2B2B2B" stroke-linecap="round"/><path fill-rule="evenodd" clip-rule="evenodd" d="M20.75 15.125C20.75 13.6753 21.9253 12.5 23.375 12.5C24.8247 12.5 26 13.6753 26 15.125C26 16.5747 24.8247 17.75 23.375 17.75C22.6985 17.75 22.0818 17.4941 21.6163 17.0738L17.7289 19.6655C17.7428 19.775 17.75 19.8867 17.75 20C17.75 20.1133 17.7428 20.225 17.7289 20.3345L21.6163 22.9262C22.0818 22.5059 22.6985 22.25 23.375 22.25C24.8247 22.25 26 23.4253 26 24.875C26 26.3247 24.8247 27.5 23.375 27.5C21.9253 27.5 20.75 26.3247 20.75 24.875C20.75 24.6446 20.7797 24.4212 20.8354 24.2083L17.1062 21.7222C16.6249 22.2753 15.9158 22.625 15.125 22.625C13.6753 22.625 12.5 21.4497 12.5 20C12.5 18.5503 13.6753 17.375 15.125 17.375C15.9158 17.375 16.6249 17.7247 17.1062 18.2778L20.8354 15.7917C20.7797 15.5788 20.75 15.3554 20.75 15.125ZM23.375 14C22.7537 14 22.25 14.5037 22.25 15.125C22.25 15.7463 22.7537 16.25 23.375 16.25C23.9963 16.25 24.5 15.7463 24.5 15.125C24.5 14.5037 23.9963 14 23.375 14ZM15.125 18.875C14.5037 18.875 14 19.3787 14 20C14 20.6213 14.5037 21.125 15.125 21.125C15.7463 21.125 16.25 20.6213 16.25 20C16.25 19.3787 15.7463 18.875 15.125 18.875ZM22.25 24.875C22.25 24.2537 22.7537 23.75 23.375 23.75C23.9963 23.75 24.5 24.2537 24.5 24.875C24.5 25.4963 23.9963 26 23.375 26C22.7537 26 22.25 25.4963 22.25 24.875Z" fill="#2B2B2B"/></svg></a>`;
  }

  /**
   * Render material item template (mimics Liquid snippet)
   * @param {any} product - Product data
   * @returns {string} HTML string
   * @private
   */
  // @ts-ignore
  async renderMaterialItemTemplate(product, sectionData, instanceId = '') {
    // Extract product data
    const hasMaterialType =
      product.material_type && product.material_type.trim() !== '';
    const badgeClass = hasMaterialType
      ? product.material_type.toLowerCase()
      : '';
    const badgeText = hasMaterialType
      ? product.material_type.toUpperCase()
      : '';
    const authorName = this.productHandler.extractAuthorName(product) || '';
    const edition = this.productHandler.extractEditionFromTitle(product) || 'Not specified';
    const isbn = this.productHandler.extractISBNFromProduct(product) || 'Not specified';
    // @ts-ignore
    const formatButtons = this.productHandler.generateFormatButtons(product);
    const prod_formats = this.productHandler.getVariantUniques(product);

    const cm = product?.metafields?.course_materials || product?.metafields?.course_materials?.value;
    const isBundleParent =
      cm?.is_package_flag === true || String(cm?.is_package_flag).toLowerCase() === 'true';
    const bundleCourseMessageHtml = isBundleParent
      ? '<p class="bundle-course-message" tabindex="0">This course uses a bundle chosen by your instructor.</p>'
      : '';

    // Badge HTML (conditional)
    let bagdeHtmlData = ''
    if(hasMaterialType){
      if(badgeClass =="choice" || badgeClass =="CHOICE" || badgeClass =="choice/required" || badgeClass =="CHOICE/REQUIRED"){
        bagdeHtmlData =  `
          <div class="material-badge-choice" tabindex="0" aria-label="Required, Choice">
            <div class="material-badge required" aria-hidden="true">
                <span class="badge-text">Required</span>
            </div>
            <div class="material-badge choice" aria-hidden="true">
                <span class="badge-text">CHOICE</span>
            </div>
          </div>
        `
      }else if(badgeClass =="choice/recommended"){
        bagdeHtmlData =  `
          <div class="material-badge-choice" tabindex="0" aria-label="Recommended, Choice">
            <div class="material-badge recommended" aria-hidden="true">
                <span class="badge-text">Recommended</span>
            </div>
            <div class="material-badge choice" aria-hidden="true">
                <span class="badge-text">CHOICE</span>
            </div>
          </div>
        `
      }else if(badgeText == "SUGGESTED"){
        bagdeHtmlData =  `
            <div class="material-badge recommended" tabindex="0" aria-label="Suggested">
                <span class="badge-text" aria-hidden="true">Suggested</span>
            </div>
        `
      }else{
        const displayBadgeText =
          badgeClass === 'required'
            ? 'Required'
            : badgeClass === 'recommended'
              ? 'Recommended'
              : badgeText;
        const badgeLabel = String(displayBadgeText).replace(/"/g, '&quot;');
        bagdeHtmlData =  `
            <div class="material-badge ${badgeClass}" tabindex="0" aria-label="${badgeLabel}">
                <span class="badge-text" aria-hidden="true">${displayBadgeText}</span>
            </div>
        `
      }
    }
    const badgeHTML = hasMaterialType
      ? bagdeHtmlData
      : '';

    let htmlOutput;

    const showDigitalDeliveryNotice =
      await ProductDisplayManager.getDigitalDeliveryNoticeVisible();
    const digitalDeliveryNoticeHtml = showDigitalDeliveryNotice
      ? '<div class="digital-delivery-notice">Digital delivery fees may apply.</div>'
      : '';

    const sectionIdNumber = sectionData.courseInfo.selectedsectionId;

    const filteredSectionAccess = window.courseData.ddcs_section.find(
      section => section.section_id === sectionData.courseInfo.selectedsectionId
    );

    
    const terms = Array.isArray(window.courseData?.ddcs_term)
      ? window.courseData.ddcs_term
      : [];
    const campuses = Array.isArray(window.courseData?.ddcs_campus_entity)
      ? window.courseData.ddcs_campus_entity
      : [];
    const filteredTermSelected = terms.find(
      term => term.term_id === sectionData.courseInfo.termId
    );
    const campusEntityId =
      filteredTermSelected?.campus_entity_id ||
      filteredSectionAccess?.campus_entity_id ||
      '';
    const filteredAccessProgramName = campuses.find(
      campus => campus.campus_entity_id === campusEntityId
    );

    
    const formatAccessProgramName = (name) => {
      const trimmed = typeof name === 'string' ? name.trim() : '';
      if (!trimmed) return '';
      if (/^follett\s+access$/i.test(trimmed)) return 'Follett Access';
      return trimmed;
    };

    const includedProgramName = formatAccessProgramName(
      filteredAccessProgramName?.included_program_name,
    );
    const accessProgramNote =
      includedProgramName ||
      formatAccessProgramName(filteredSectionAccess?.included_section_note);
    
    let mapped_section = null;
    
    // Validate and parse section_data_mapping
    if (product.metafields?.course_materials?.section_data_mapping) {
        try {
            const section_data_mapping = JSON.parse(product.metafields.course_materials.section_data_mapping);
            
            // Check if it's an array with values
            if (Array.isArray(section_data_mapping) && section_data_mapping.length > 0) {
                // Find element with SectionID equal to sectionIdNumber
                mapped_section = section_data_mapping.find(item => item.SectionID === sectionIdNumber);
                
            }
        } catch (error) {
            console.error("Error parsing section_data_mapping:", error);
        }
    }

            
    // Check if mapped_section exists and IncludEDMaterialFlag is true
    const accessProgramBanner = (mapped_section && (mapped_section.IncludEDMaterialFlag === "true" || mapped_section.IncludEDMaterialFlag === true)) 
      ? `<div class="rental-banner access-program-style" tabindex="0">
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true" focusable="false">
        <path fill-rule="evenodd" clip-rule="evenodd" d="M8.03751 3.23346C7.81155 3.23346 7.61727 3.41091 7.61727 3.6432V3.9899H7.49033C7.1739 3.9899 6.8716 4.11407 6.64959 4.33328C6.42775 4.55234 6.30402 4.84839 6.30402 5.15606C6.30402 5.46373 6.42775 5.75978 6.64959 5.97884C6.8716 6.19806 7.1739 6.32223 7.49033 6.32223H7.61727V7.01562H6.72426C6.4983 7.01562 6.30402 7.19307 6.30402 7.42536C6.30402 7.65765 6.4983 7.83509 6.72426 7.83509H7.61727V8.18179C7.61727 8.41408 7.81155 8.59153 8.03751 8.59153C8.26348 8.59153 8.45775 8.41408 8.45775 8.18179V7.83509H8.5847C8.90113 7.83509 9.20342 7.71092 9.42543 7.4917C9.64728 7.27265 9.771 6.9766 9.771 6.66893C9.771 6.36126 9.64728 6.06521 9.42543 5.84615C9.20342 5.62693 8.90113 5.50276 8.5847 5.50276H8.45775V4.80936H9.13189C9.35786 4.80936 9.55213 4.63192 9.55213 4.39963C9.55213 4.16734 9.35786 3.9899 9.13189 3.9899H8.45775V3.6432C8.45775 3.41091 8.26348 3.23346 8.03751 3.23346ZM7.24768 4.90908C7.31298 4.84461 7.40037 4.80936 7.49033 4.80936H7.61727V5.50276H7.49033C7.40037 5.50276 7.31298 5.46752 7.24768 5.40304C7.18218 5.33836 7.1445 5.2496 7.1445 5.15606C7.1445 5.06252 7.18218 4.97376 7.24768 4.90908ZM8.45775 7.01562V6.32223H8.5847C8.67465 6.32223 8.76205 6.35747 8.82734 6.42195C8.89285 6.48663 8.93052 6.57539 8.93052 6.66893C8.93052 6.76247 8.89285 6.85123 8.82734 6.91591C8.76205 6.98038 8.67465 7.01562 8.5847 7.01562H8.45775Z" fill="#204A65"/>
        <path fill-rule="evenodd" clip-rule="evenodd" d="M9.61333 12.6985C9.61333 12.9741 9.39751 13.1976 9.13129 13.1976H6.85164C6.58542 13.1976 6.3696 12.9741 6.3696 12.6985C6.3696 12.4229 6.58542 12.1995 6.85164 12.1995H9.13129C9.39751 12.1995 9.61333 12.4229 9.61333 12.6985Z" fill="#204A65"/>
        <path fill-rule="evenodd" clip-rule="evenodd" d="M9.18567 9.08436L9.68398 8.7961C10.6952 8.21114 11.3719 7.12026 11.3719 5.8725C11.3719 4.00885 9.86114 2.49807 7.9975 2.49807C6.13385 2.49807 4.62307 4.00885 4.62307 5.8725C4.62307 7.12026 5.29978 8.21114 6.31101 8.7961L6.80932 9.08435V10.6094C6.80932 10.6619 6.85187 10.7044 6.90437 10.7044H9.09062C9.14312 10.7044 9.18567 10.6619 9.18567 10.6094V9.08436ZM10.1837 9.66004V10.6094C10.1837 11.2131 9.69433 11.7025 9.09062 11.7025H6.90437C6.30065 11.7025 5.81125 11.2131 5.81125 10.6094V9.66003C4.50431 8.90401 3.625 7.49094 3.625 5.8725C3.625 3.45763 5.58263 1.5 7.9975 1.5C10.4124 1.5 12.37 3.45763 12.37 5.8725C12.37 7.49094 11.4907 8.90401 10.1837 9.66004Z" fill="#204A65"/>
      </svg>
      <span class="access-program-text-product">
        ${ProductDisplayManager.fillAccessProgramPlaceholderForHtml(
          ProductDisplayManager.getAccessProgramMaterialBannerTemplate(),
          formatAccessProgramName(accessProgramNote)
        )}
      </span>                   
    </div>` 
    : '';

    if(product.hideSalesChannel && product.variants.length === 0){
      htmlOutput = `${accessProgramBanner}`;
    }else{
      if (prod_formats.length === 1) {
          if (prod_formats[0] === "Digital") {
              const initialPurchaseOptions = await this.productHandler.generateInitialPurchaseOptions("Digital", product, sectionData);
              htmlOutput = `
          ${accessProgramBanner}
            ${product.hideSalesChannel ? `` : `
              ${buildFormatPurchasePanelHtml({
                format: 'digital',
                label: 'Digital',
                productId: product.id,
                includeWarning: true,
                collapseContentHtml: `
                            ${digitalDeliveryNoticeHtml}
                            <div class="purchase-types">
                                ${initialPurchaseOptions}
                            </div>`,
              })}
            <div class="material-notify-container" style="display: none;">
                <!-- Notify me button will be added here when needed -->
            </div>
            `}
          `;
          } else if (prod_formats[0] === "Print") {
              const print = await this.productHandler.generateInitialPurchaseOptions("Print", product, sectionData);
              htmlOutput = `
          ${accessProgramBanner}
          ${buildFormatPurchasePanelHtml({
            format: 'print',
            label: 'Physical',
            productId: product.id,
            includeWarning: true,
            collapseContentHtml: `
                          <div class="purchase-types">
                              ${print}
                          </div>`,
          })}
          <div class="material-notify-container" style="display: none;">
              <!-- Notify me button will be added here when needed -->
          </div>`;
          } else {
            const initialPurchaseOptions = await this.productHandler.generateInitialPurchaseOptions("", product, sectionData);
            const simpleGroupId = buildPurchaseGroupLabelId({
              format: 'options',
              productId: product.id,
            });
            htmlOutput = `
              <!-- Simple product - direct purchase options -->
              ${accessProgramBanner}
              ${product.hideSalesChannel ? `` : `
              <div class="simple-product-purchase" role="group" aria-labelledby="${simpleGroupId}">
                <h4 id="${simpleGroupId}" class="cmrp-purchase-group-label">Purchase options</h4>
                ${MULTI_FORMAT_SELECTION_WARNING_HTML}
                <div class="format-content-area">
                    <div class="format-collapse active" style="display: block;">
                        <div class="collapse-content">
                            <div class="purchase-types">
                              ${initialPurchaseOptions}
                            </div>
                        </div>
                    </div>
                </div>
              </div>
              <div class="material-notify-container" style="display: none;">
                <!-- Notify me button will be added here when needed -->
              </div>
              `}
            `;
          }
      } else if (prod_formats.length === 2 && prod_formats.includes("Digital") && prod_formats.includes("Print")) {
          const digital = await this.productHandler.generateInitialPurchaseOptions("Digital", product, sectionData);
          const print = await this.productHandler.generateInitialPurchaseOptions("Print", product, sectionData);
          const showDigitalPanel = !product.hideSalesChannel;
          const printPanel = buildFormatPurchasePanelHtml({
            format: 'print',
            label: 'Physical',
            productId: product.id,
            includeWarning: true,
            collapseContentHtml: `
                          <div class="purchase-types">
                              ${print}
                          </div>`,
          });
          const digitalPanel = showDigitalPanel
            ? buildFormatPurchasePanelHtml({
                format: 'digital',
                label: 'Digital',
                productId: product.id,
                collapseContentHtml: `
                          ${digitalDeliveryNoticeHtml}
                          <div class="purchase-types">
                              ${digital}
                          </div>`,
                footerHtml: `
              <div class="delivery-notice" data-format-specific="digital" style="display: none;">
                  <span id="digital-fee-notice">*Digital delivery fee of $<span class="digital-fee-amount">5.99</span> may apply</span> | 
                  <button type="button" class="requirements-link" onclick="openRequirementsDialog()">
                      Requirements
                  </button>
              </div>`,
              })
            : '';
          htmlOutput = `
          ${accessProgramBanner}
          ${printPanel}
          ${digitalPanel}
          <div class="material-notify-container" style="display: none;">
              <!-- Notify me button will be added here when needed -->
          </div>`;
      } 
    }

    const details = this._extractProductDetails(product);
    const detailsData = this._generateDetailsHTML(details);
    const isAdoptedTitle = product.tags && product.tags.includes("adoptedsupply");
    
    const materialTitleId = buildMaterialTitleId({ productId: product.id, instanceId });

    // Main template for material item rendering - MATH 100 style
    return `
        <div class="material-information" onclick="handleMaterialItemClick(event)">
            <div class="material-content">
                <div class="material-image-container">
                    <img src="${product.image}" alt="" class="material-image" />
                    ${this.materialPdpLinkHTML(product.url, product.title)}
                    <button class="material-image-plus-button" type="button" aria-label="Open purchase options for ${String(product.title || '').replace(/"/g, '&quot;')}" onclick="togglePurchaseOptions(this)">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true" focusable="false">
                            <path d="M8 3.5V12.5M3.5 8H12.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                    </button>
                </div>
                <div class="material-text-content">
                    <div class="material-text">
                        ${badgeHTML}
                        <div role="article" aria-labelledby="${materialTitleId}">
                        <h3 class="material-title" tabindex="0" id="${materialTitleId}">${product.title}</h3>
                        <div class="material-details">
                            <div class="material-author">${authorName || ''}</div>
                            ${edition !== "Not specified" ? `<div class="material-edition">Edition: ${edition}</div>` : ''}
                            ${isbn !== "Not specified" && isAdoptedTitle === false ? `<div class="material-isbn">ISBN: ${isbn}</div>` : ''}
                            ${detailsData !== "" ? `<div class="material-view-more">
                                <button class="view-more-btn" type="button" aria-expanded="false">
                                    <span>View more details</span>
                                    <svg class="chevron-down" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true" focusable="false">
                                        <path d="M4 6L8 10L12 6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                                    </svg>
                                </button>
                            </div>`: ''}
                            
                            <div class="material-view-options" onclick="togglePurchaseOptions(this)">
                                <span>View options</span>
                                <svg class="chevron-right" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true" focusable="false">
                                    <path d="M6 4L10 8L6 12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                                </svg>
                            </div>
                        </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div class="material-purchase-column">
          <div class="material-purchase-options">
              ${bundleCourseMessageHtml}
              ${htmlOutput}
          </div>
        </div>
    `;
  }

  /**
   * Get adoption status info based on note content
   * @param {string} note - Adoption status note
   * @returns {Object} Status info with type and icon
   */
  getAdoptionStatusInfo(note) {
    const lowerNote = note.toLowerCase();

    if (!this.ADOPTION_STATUS_CONFIG)
      return { type: 'pending', icon: this.ADOPTION_STATUS_ICON };

    for (const [statusType, config] of Object.entries(
      this.ADOPTION_STATUS_CONFIG
    )) {
      if (config.keywords.some(keyword => lowerNote.includes(keyword))) {
        return { type: statusType, icon: this.ADOPTION_STATUS_ICON };
      }
    }

    return { type: 'pending', icon: this.ADOPTION_STATUS_ICON };
  }

  /**
   * Create adoption card HTML (mimics snippets/adoption-card.liquid)
   * @param {any} filter - Filter object
   * @param {any} section - Section object
   * @param {string} courseCode - Course code (unused but kept for compatibility)
   * @param {string} note - Adoption note
   * @param {any} statusInfo - Status info
   * @returns {string} HTML string for adoption card
   */
  // @ts-ignore
  // @ts-ignore
  // @ts-ignore
  createAdoptionCardHTML(filter, section, courseCode, note, statusInfo) {
    const instructorName = section
      ? `${section.instructor_first_name || ''} ${
          section.instructor_last_name || ''
        }`.trim() || 'TBA'
      : 'TBA';
    const sectionNumber = section?.display_name || filter.selectedsection;
    // Adoption / no-required cards: omit the required count (only show when > 0).
    const safeNote = String(note || '').replace(/</g, '&lt;').replace(/>/g, '&gt;');

    return `
        <div class="course-card adoption-card" data-status="${statusInfo.type}">
            <div class="course-header">
                <div class="header-content">
                    <div class="course-details">
                        <div class="course-title">${filter.selectedDepartment} ${filter.selectedcourse}</div>
                        <div class="section-info">Section: ${sectionNumber}${instructorName === 'TBA' ? '' : ` | Instructor: ${instructorName}`}</div>
                    </div>
                    <div class="header-actions">
                        <button class="remove-btn" type="button" aria-label="Remove course"
                                onclick="window.removeCourse(this)" 
                                data-division="${filter.selectedDivision || ''}"
                                data-department="${filter.selectedDepartment}" 
                                data-course="${filter.selectedcourse}" 
                                data-section="${filter.selectedsection}"
                                data-section-id="${filter.selectedsectionId || ''}"
                                data-section-gid="${filter.selectedsectionGid || ''}">
                            <svg class="remove-icon" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                                <path d="M13.5 4.5L4.5 13.5M4.5 4.5L13.5 13.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                            </svg>
                        </button>
                    </div>
                </div>
            </div>
            <div class="materials-section">
                <div class="no-materials-message">
                    <div class="adoption-message-content">
                        <div class="adoption-icon">${statusInfo.icon}</div>
                        <div class="adoption-text">
                            <h4 class="adoption-title">Course Materials Status</h4>
                            <p class="adoption-note">${safeNote}</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    `;
  }

  /**
   * Utility methods for state management (delegated to external functions)
   */
  showEmptyState() {
    /** @type {any} */
    const globalWindow = window;
    if (typeof globalWindow.showEmptyState === 'function') {
      globalWindow.showEmptyState();
    }
  }

  hideEmptyState() {
    /** @type {any} */
    const globalWindow = window;
    if (typeof globalWindow.hideEmptyState === 'function') {
      globalWindow.hideEmptyState();
    }
  }

  hideLoadingState() {
    /** @type {any} */
    const globalWindow = window;
    if (typeof globalWindow.hideLoadingState === 'function') {
      globalWindow.hideLoadingState();
    }
  }

  async handleEmptyFiltersState() {
    const hasCourseBuilderForm = Boolean(
      document.querySelector('.cmrp-filter-form--course-builder'),
    );
    let campusSelect;
    let termSelect;
    if (
      hasCourseBuilderForm &&
      typeof window.cmrpCourseBuilderCampusTermEl === 'function'
    ) {
      campusSelect = window.cmrpCourseBuilderCampusTermEl('campus');
      termSelect = window.cmrpCourseBuilderCampusTermEl('term');
    } else {
      campusSelect =
        typeof window.cmrpQueryDesktopCampusTerm === 'function'
          ? window.cmrpQueryDesktopCampusTerm('campus')
          : document.querySelector('#campus-term-dropdowns #campus');
      termSelect =
        typeof window.cmrpQueryDesktopCampusTerm === 'function'
          ? window.cmrpQueryDesktopCampusTerm('term')
          : document.querySelector('#campus-term-dropdowns #term');
    }

    if (campusSelect?.value && termSelect?.value) {
      if (typeof window.courseMaterialsSidebar?.resetCourseBuilderLineKeepCampusTerm === 'function') {
        try {
          await window.courseMaterialsSidebar.resetCourseBuilderLineKeepCampusTerm();
        } catch (e) {
          console.error('[CMRP] handleEmptyFiltersState reset failed', e);
        }
      }
    } else if (!campusSelect?.value) {
      if (window.courseMaterialsSidebar) {
        window.courseMaterialsSidebar.populateCampus();
      }
    }
  }

  /**
   * Update In Bag indicators for the current section
   * @returns {void}
   */
  updateInBagIndicatorsForSection() {
    const manager =
      /** @type {any} */ (window).courseMaterialsInteractionManager ||
      /** @type {any} */ (window).courseMaterialsPage?.components
        ?.interactionManager;

    if (manager && typeof manager.updateAllInBagIndicators === 'function') {
      // Small delay to ensure DOM is fully rendered
      setTimeout(() => {
        manager.updateAllInBagIndicators();
      }, 100);
    }
  }

  /**
   * Theme setting: SAM SKUs (variant SKU must match). Used to hide placeholder materials.
   * @returns {Set<string>}
   */
  static getCmrpSamSkuSet() {
    const raw =
      typeof window !== 'undefined' ? window.shopifySettings?.cmrpSamSkus : null;
    if (!Array.isArray(raw) || raw.length === 0) return new Set();
    return new Set(raw.map(s => String(s).trim()).filter(Boolean));
  }

  /**
   * Theme setting: message when only SAM SKU material(s) apply (replaces generic empty / 400 copy).
   * @returns {string}
   */
  static getCmrpSamHiddenMessage() {
    const g = typeof window !== 'undefined' ? window.shopifySettings : null;
    const msg =
      g && typeof g.cmrpSamSkuHiddenMessage === 'string'
        ? g.cmrpSamSkuHiddenMessage.trim()
        : '';
    return msg || 'No materials required from the bookstore.';
  }

  /**
   * Access copy from theme settings (schema / Liquid defaults).
   * @param {string} key
   * @returns {string}
   */
  static getAccessProgramSetting(key) {
    const g = typeof window !== 'undefined' ? window.shopifySettings : null;
    return g && typeof g[key] === 'string' ? g[key].trim() : '';
  }

  /**
   * @returns {string}
   */
  static getAccessProgramDefaultName() {
    return ProductDisplayManager.getAccessProgramSetting('cmrpAccessProgramDefaultName') || 'Follett Access';
  }

  /**
   * @param {string} label
   * @returns {string}
   */
  static resolveAccessProgramLabel(label) {
    const value = String(label ?? '').trim();
    if (!value) return ProductDisplayManager.getAccessProgramDefaultName();
    if (/^follett\s+access$/i.test(value)) return 'Follett Access';
    return value;
  }

  /**
   * @returns {string}
   */
  static getAccessProgramBannerTemplate() {
    return ProductDisplayManager.getAccessProgramSetting('cmrpAccessProgramBannerText');
  }

  /**
   * @returns {string}
   */
  static getAccessProgramTitleTemplate() {
    return ProductDisplayManager.getAccessProgramSetting('cmrpAccessProgramTitleTemplate');
  }

  /**
   * @returns {string}
   */
  static getAccessProgramBodyTemplate() {
    return ProductDisplayManager.getAccessProgramSetting('cmrpAccessProgramBodyText');
  }

  /**
   * @returns {string}
   */
  static getAccessProgramMaterialBannerTemplate() {
    return ProductDisplayManager.getAccessProgramSetting('cmrpAccessProgramMaterialBannerText');
  }

  /**
   * @param {string} template
   * @param {string} label
   * @returns {string}
   */
  static fillAccessProgramPlaceholder(template, label) {
    return String(template ?? '').replace(
      /\[Access Program\]/g,
      ProductDisplayManager.resolveAccessProgramLabel(label)
    );
  }

  /**
   * @param {unknown} value
   * @returns {string}
   */
  static escapeAccessProgramHtml(value) {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /**
   * Fill [Access Program] after HTML-escaping the template. Optionally wrap the
   * program name in the tooltip title span.
   * @param {string} template
   * @param {string} label
   * @param {boolean} [wrapTitle]
   * @returns {string}
   */
  static fillAccessProgramPlaceholderForHtml(template, label, wrapTitle = false) {
    const resolved = ProductDisplayManager.escapeAccessProgramHtml(
      ProductDisplayManager.resolveAccessProgramLabel(label)
    );
    const replacement = wrapTitle
      ? `<span class="access-program-title">${resolved}</span>`
      : resolved;
    return ProductDisplayManager.escapeAccessProgramHtml(String(template ?? '')).replace(
      /\[Access Program\]/g,
      replacement
    );
  }

  /**
   * @param {any} product
   * @param {Set<string>} samSet
   * @returns {boolean}
   */
  static productHasSamSku(product, samSet) {
    const set = samSet || ProductDisplayManager.getCmrpSamSkuSet();
    if (!set.size || !product?.variants?.length) return false;
    for (const v of product.variants) {
      const sku = String(v?.sku ?? '').trim();
      if (sku && set.has(sku)) return true;
    }
    return false;
  }
}

// Global function to toggle purchase options on mobile
// @ts-ignore
window.togglePurchaseOptions = function(button) {
  const materialItem = button.closest('.material-item');
  if (!materialItem) return;
  
  const purchaseOptions = materialItem.querySelector('.material-purchase-options');
  if (!purchaseOptions) return;
  
  const isExpanded = purchaseOptions.classList.contains('show');
  
  if (isExpanded) {
    // Hide options
    purchaseOptions.classList.remove('show');
    button.classList.remove('expanded');
  } else {
    // Show options
    purchaseOptions.classList.add('show');
    button.classList.add('expanded');
  }
};

// Global function to handle material item click on mobile
// @ts-ignore
window.handleMaterialItemClick = function(event) {
  // Only handle on mobile devices
  if (window.innerWidth > 968) return;
  
  // Check if the click target is an interactive element
  const interactiveElements = [
    '.material-image-plus-button',
    '.choice-product-card__plus-select',
    '.choice-product-card__view-options',
    '.choice-product-card__edit',
    '.choice-product-card__remove',
    '.material-view-options', 
    '.view-more-btn',
    '.purchase-checkbox',
    '.requirements-link',
    '.format-tab',
    '.purchase-type',
    '.checkbox-label'
  ];
  
  const isInteractiveElement = interactiveElements.some(selector => 
    event.target.closest(selector)
  );
  
  if (isInteractiveElement) return;
  
  // Prevent default behavior
  event.preventDefault();
  
  // Find the material item and toggle purchase options
  const materialItem = event.currentTarget;
  const purchaseOptions = materialItem.querySelector('.material-purchase-options');
  
  if (!purchaseOptions) return;
  
  const isExpanded = purchaseOptions.classList.contains('show');
  
  if (isExpanded) {
    // Hide options
    purchaseOptions.classList.remove('show');
  } else {
    // Show options
    purchaseOptions.classList.add('show');
  }
};

// Export for use in other modules
/** @type {any} */
const globalWindowExport = window;
globalWindowExport.ProductDisplayManager = ProductDisplayManager;

// Create namespace for the system
/** @type {any} */
const globalWindow = window;
if (!globalWindow.ProductDisplay) {
  globalWindow.ProductDisplay = {};
}

globalWindow.ProductDisplay.ProductDisplayManager = ProductDisplayManager;